import React, { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { auth, firestore } from "../../firebase"
import {
  collection,
  doc,
  serverTimestamp,
  writeBatch,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
} from "firebase/firestore"
import { handleGoogleSignIn as authGoogleSignIn } from "../../utils/auth"
import { useTryonData } from "../../hooks/useFirestoreImages"
import { useUserBalance } from "../../hooks/useUserBalance"
import { useTemplateGallery } from "../../hooks/useTemplateGallery"
import { uploadToR2 } from "../../uploadToR2"
import ImageGallery from "../../components/tryon/ImageGallery"

// --- Modes (designed to be extended later) ---
const MODES = [
  { id: "motion-control", label: "Motion Control", available: true },
  { id: "images", label: "Create Character", available: true },
]

// --- Trending characters for Create Character mode ---
const CHARACTERS = [
  {
    id: "barbie",
    label: "Barbie",
    normalPhrase: "a barbie doll",
    transferPhrase: "make the people in the image into a barbie doll",
  },
  {
    id: "shrek",
    label: "Shrek",
    normalPhrase: "Shrek the ogre",
    transferPhrase: "make the people in the image into Shrek the ogre",
  },
  {
    id: "fruit",
    label: "Fruit with face",
    normalPhrase: "a fruit with a face",
    transferPhrase:
      "make the people in the image into a fruit with a face",
  },
  {
    id: "sims",
    label: "Sims",
    normalPhrase: "a Sims character with a glowing green plumbob floating above their head",
    transferPhrase:
      "make the people in the image into Sims characters with glowing green plumbobs floating above their heads",
  },
]
// Sorted longest-first so substring strips remove transfer phrases
// (which contain the normal phrase) before the shorter normal ones.
const ALL_CHARACTER_PHRASES = CHARACTERS.flatMap((c) => [
  c.normalPhrase,
  c.transferPhrase,
]).sort((a, b) => b.length - a.length)
const characterPhraseFor = (id, transfer) => {
  const c = CHARACTERS.find((x) => x.id === id)
  if (!c) return null
  return transfer ? c.transferPhrase : c.normalPhrase
}

// --- Motion video examples ---
// The "add your own" tile is rendered separately. Only one real example exists right now.
const MOTION_EXAMPLES = [
  {
    id: "ice-stumble-barbie",
    name: "I tremble",
    badge: "Trending",
    motionVideoUrl: "https://r2.pinkcatai.com/tremble/tremble.mp4",
    previewImage: "https://r2.pinkcatai.com/Media/IMG_3735.jpg",
  },
]

const VIDEO_CREDIT_COST = 30
const VIDEO_CREDIT_COST_BASIC = 15
const IMAGE_CREDIT_COST = 1

// Motion-control input must not exceed this duration. Videos longer than
// MOTION_VIDEO_MAX_SECONDS are auto-trimmed (server-side) to MOTION_VIDEO_TRIM_SECONDS.
const MOTION_VIDEO_MAX_SECONDS = 30
const MOTION_VIDEO_TRIM_SECONDS = 29

// Direct Cloud Run URLs for long-running Cloud Functions. We deliberately
// bypass the Firebase Hosting rewrite (`/extract_tiktok_video`,
// `/trim_video_to_max`) here, because Hosting forces a 60-second gateway
// timeout that returns HTTP 502 even though the underlying function has
// `timeout_sec=300`. yt-dlp + ffmpeg trim of a long TikTok regularly
// exceeds 60 s. Both functions set permissive CORS headers, so cross-
// origin calls from the SPA work fine.
const EXTRACT_TIKTOK_VIDEO_URL =
  "https://extract-tiktok-video-va6re4odzq-uc.a.run.app"
const TRIM_VIDEO_TO_MAX_URL =
  "https://trim-video-to-max-va6re4odzq-uc.a.run.app"

// Reads the duration of a local video File via a hidden <video> element.
// Returns the duration in seconds, or null if it can't be determined.
const getVideoDurationFromFile = (file) =>
  new Promise((resolve) => {
    let done = false
    let url = null
    const finish = (val) => {
      if (done) return
      done = true
      if (url) {
        try { URL.revokeObjectURL(url) } catch (_) {}
      }
      resolve(val)
    }
    try {
      url = URL.createObjectURL(file)
      const video = document.createElement("video")
      video.preload = "metadata"
      video.muted = true
      video.onloadedmetadata = () => {
        const d = Number(video.duration)
        finish(Number.isFinite(d) && d > 0 ? d : null)
      }
      video.onerror = () => finish(null)
      setTimeout(() => finish(null), 8000)
      video.src = url
    } catch (_) {
      finish(null)
    }
  })

const PINK = "#ff4fb8"
const PINK_DEEP = "#d96a93"
const PINK_BORDER = "#f4c5d6"
const PINK_BG_SOFT = "#fff5f9"
const TEXT_DARK = "#4a1a2c"
const TEXT_MUTED = "#8b4d6a"
// Strong accent used to clearly highlight the active "reference image" tile.
const VIOLET = "#7c3aed"
const VIOLET_DEEP = "#5b21b6"
const VIOLET_BG_SOFT = "#f3e8ff"

const Dashboard = () => {
  const navigate = useNavigate()
  const [error, setError] = useState("")
  const [userId, setUserId] = useState(null)
  const balance = useUserBalance(userId)
  const { galleryImages, isLoadingInitial, refreshTryonData } = useTryonData()
  const { templateImages } = useTemplateGallery()

  const exampleStyleTemplates = useMemo(() => {
    const isExampleCategory = (cat) => {
      if (!cat) return false
      if (Array.isArray(cat))
        return cat.map((c) => String(c).toLowerCase()).includes("example")
      return String(cat).toLowerCase() === "example"
    }
    return (templateImages || []).filter((t) =>
      isExampleCategory(t.category)
    )
  }, [templateImages])

  // Full character gallery — every template that's NOT a flagged "example" style
  // (the four style examples are reserved for the Style picker / filters).
  const characterGalleryTemplates = useMemo(() => {
    const isExampleCategory = (cat) => {
      if (!cat) return false
      if (Array.isArray(cat))
        return cat.map((c) => String(c).toLowerCase()).includes("example")
      return String(cat).toLowerCase() === "example"
    }
    return (templateImages || []).filter(
      (t) => !isExampleCategory(t.category)
    )
  }, [templateImages])

  // Orientation/reference picker — reuse the templates collection.
  // Each entry needs a small `thumbUrl` (for the 80×80 preview tile) and a
  // `fullUrl` (the high-res image we actually feed into the model).
  const orientationTemplates = useMemo(() => {
    const isExampleCategory = (cat) => {
      if (!cat) return false
      if (Array.isArray(cat))
        return cat.map((c) => String(c).toLowerCase()).includes("example")
      return String(cat).toLowerCase() === "example"
    }
    return (templateImages || [])
      .filter((t) => !isExampleCategory(t.category))
      .map((t) => ({
        id: t.id,
        thumbUrl: t.thumbnailUrl || t.url,
        fullUrl: t.url,
      }))
  }, [templateImages])

  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  )

  const [activeMode, setActiveMode] = useState("motion-control")
  // Images-mode state (Describe your character)
  const [imagePrompt, setImagePrompt] = useState("")
  const [selectedImageTags, setSelectedImageTags] = useState([])
  // Motion-control-mode state (Describe your video)
  const [motionPrompt, setMotionPrompt] = useState("")
  const [selectedMotionTags, setSelectedMotionTags] = useState([])
  const [motionAdvancedOpen, setMotionAdvancedOpen] = useState(false)
  // "pro" = recommended tier (default, full credits). "basic" = lighter tier (half credits).
  // The actual model identifier is resolved server-side so vendor names never leak to the client.
  const [motionModelTier, setMotionModelTier] = useState("pro")
  const [selectedImageFilterId, setSelectedImageFilterId] = useState(null)
  const [selectedCharacterId, setSelectedCharacterId] = useState(null)
  const [transferPeople, setTransferPeople] = useState(false)
  const [orientationImageUrl, setOrientationImageUrl] = useState(null)
  const [orientationUploading, setOrientationUploading] = useState(false)
  // Lokale Blob-URL, die wir direkt nach Auswahl der Datei anzeigen, damit der
  // User auf dem Smartphone die Vorschau sofort sieht — ohne auf den
  // R2-Upload (oder einen Login-Redirect) warten zu müssen.
  const [characterImageUrl, setCharacterImageUrl] = useState(null)
  const [characterLocalPreview, setCharacterLocalPreview] = useState(null)
  const [characterUploading, setCharacterUploading] = useState(false)
  const [orientationLocalPreview, setOrientationLocalPreview] = useState(null)
  const [selectedMotion, setSelectedMotion] = useState(MOTION_EXAMPLES[0]) // default-pick the trending one
  // The user's own motion video (resolved to an R2 URL — either via upload or via TikTok import).
  const [customMotionUrl, setCustomMotionUrl] = useState(null)
  const [customMotionLabel, setCustomMotionLabel] = useState("")
  const [customMotionSource, setCustomMotionSource] = useState(null) // 'upload' | 'tiktok' | null
  // Persistente Galerie aller bisher hochgeladenen / per TikTok importierten
  // Motion-Videos des Users. Wird live aus Firestore geladen, damit der User
  // alte Imports erneut auswählen kann, ohne sie nochmal hochzuladen.
  const [savedMotionVideos, setSavedMotionVideos] = useState([])
  const [isGenerating, setIsGenerating] = useState(false)
  const [hiddenKeys, setHiddenKeys] = useState(new Set())
  const [fullscreenImage, setFullscreenImage] = useState(null)
  const [showCharTooltip, setShowCharTooltip] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  // Brief confirmation toast right after Generate is clicked, so the user
  // always gets visible feedback that the request was accepted (in case the
  // gallery item appears slowly or the listener has a hiccup).
  const [submittedToast, setSubmittedToast] = useState(null) // { kind: 'video' | 'image' } | null
  // Tracks whether we've already attempted to hydrate the draft from
  // localStorage. We must not write any draft back to storage until this is
  // true, otherwise the *defaults* set by the useState initializers would
  // overwrite a real draft on first render (before the load effect runs).
  const [draftHydrated, setDraftHydrated] = useState(false)

  // --- Custom motion: upload + TikTok import state ---
  const [uploadBusy, setUploadBusy] = useState(false)
  const [uploadError, setUploadError] = useState("")
  const [uploadStatus, setUploadStatus] = useState("")
  const [tiktokPanelOpen, setTiktokPanelOpen] = useState(false)
  const [tiktokUrl, setTiktokUrl] = useState("")
  const [tiktokBusy, setTiktokBusy] = useState(false)
  const [tiktokError, setTiktokError] = useState("")

  // --- Drag-over visual state for drop targets ---
  const [characterDragOver, setCharacterDragOver] = useState(false)
  const [motionDragOver, setMotionDragOver] = useState(false)
  const [orientationDragOver, setOrientationDragOver] = useState(false)

  const characterInputRef = useRef(null)
  const orientationInputRef = useRef(null)
  const motionInputRef = useRef(null)
  const tiktokInputRef = useRef(null)
  // Wenn der User noch nicht eingeloggt ist, parken wir die Datei hier und
  // schicken sie automatisch zum Upload, sobald `userId` verfügbar ist
  // (z.B. nach einem Mobile-Redirect-Login).
  const pendingCharacterFileRef = useRef(null)
  const pendingOrientationFileRef = useRef(null)

  // --- Auth subscription ---
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUserId(user ? user.uid : null)
    })
    return () => unsubscribe()
  }, [])

  // --- Saved motion videos subscription ---
  // Lade die persistierte Liste der bisher importierten Motion-Videos des
  // Users (Uploads + TikTok-Importe), damit sie als zusätzliche Tiles in der
  // "Add Motion Video"-Galerie erscheinen.
  useEffect(() => {
    if (!userId) {
      setSavedMotionVideos([])
      return undefined
    }
    const colRef = collection(firestore, "users", userId, "motionVideos")
    const q = query(colRef, orderBy("createdAt", "desc"))
    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = []
        snap.forEach((d) => {
          const data = d.data() || {}
          if (!data.url) return
          items.push({
            id: d.id,
            url: data.url,
            name: data.name || "",
            source: data.source === "tiktok" ? "tiktok" : "upload",
          })
        })
        setSavedMotionVideos(items)
      },
      (err) => {
        console.warn("[dashboard] motionVideos subscription failed:", err)
      },
    )
    return () => unsub()
  }, [userId])

  // --- Stripe checkout success: show a welcome toast when redirected back
  // from a successful subscription. The redirect URL is /dashboard?payment=success
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("payment") === "success") {
      setPaymentSuccess(true)
      const cleanUrl = window.location.pathname + window.location.hash
      window.history.replaceState({}, "", cleanUrl)
      const timer = setTimeout(() => setPaymentSuccess(false), 8000)
      return () => clearTimeout(timer)
    }
  }, [])

  // --- Auto-dismiss the "Submitted" confirmation toast after a few seconds.
  useEffect(() => {
    if (!submittedToast) return undefined
    const t = setTimeout(() => setSubmittedToast(null), 6000)
    return () => clearTimeout(t)
  }, [submittedToast])

  // --- Stuck-video auto-sync ---
  // Videos normally take ~15 min to generate. If a video has been loading
  // for > 17 min (15 + 2 min buffer), the webhook from Fal AI probably
  // failed to deliver (function timeout, network blip, …) and the doc
  // is stuck. Every 90s we ask the backend to poll Fal directly and
  // update Firestore.
  const syncedVideoIdsRef = useRef(new Set())
  useEffect(() => {
    let cancelled = false

    const trySync = async () => {
      if (!userId || !galleryImages?.length) return
      const cutoff = Date.now() - 17 * 60 * 1000 // 17 minutes
      const stuck = galleryImages.filter(
        (it) =>
          it.mediaType === "video" &&
          it.isLoading &&
          !it.isFailed &&
          it.createdAt instanceof Date &&
          it.createdAt.getTime() < cutoff &&
          !syncedVideoIdsRef.current.has(it.id)
      )
      if (stuck.length === 0) return

      let token
      try {
        token = await auth.currentUser?.getIdToken()
      } catch (_) {
        return
      }
      if (!token) return

      const apiBase =
        process.env.NODE_ENV === "production" ? "" : "https://pinkcatai.web.app"

      for (const item of stuck) {
        if (cancelled) return
        syncedVideoIdsRef.current.add(item.id)
        try {
          const res = await fetch(`${apiBase}/sync_video_status`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ videoId: item.id }),
          })
          const data = await res.json().catch(() => null)
          console.log("[dashboard] sync_video_status", item.id, data)
          // If the doc transitioned to a terminal state, force the
          // Firestore realtime listener to reconnect so the gallery
          // picks up the new state without requiring a page refresh.
          if (data?.status === "succeeded" || data?.status === "failed") {
            refreshTryonData?.()
          }
          // If Fal still says in_progress, allow the next tick (in 60s)
          // to retry. We only mark as "done trying" when the doc is in a
          // terminal state.
          if (data?.status === "in_progress") {
            syncedVideoIdsRef.current.delete(item.id)
          }
        } catch (e) {
          console.warn("[dashboard] sync_video_status failed", item.id, e)
          syncedVideoIdsRef.current.delete(item.id)
        }
      }
    }

    trySync()
    const interval = setInterval(trySync, 90 * 1000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [userId, galleryImages])

  // --- Draft persistence ---
  // We persist TEXT-only state (prompt, tags, mode, filters) across reloads
  // and tabs so users don't lose typing. We deliberately do NOT persist
  // uploaded media URLs (character image, orientation image, custom motion
  // video) — those should always start fresh on a new visit / refresh /
  // new tab so users don't see a stale image from a previous session.
  // Drafts older than DRAFT_TTL_MS are discarded.
  useEffect(() => {
    const DRAFT_KEY = "pinkcatai.dashboard.draft.v1"
    const DRAFT_TTL_MS = 24 * 60 * 60 * 1000 // 24h
    try {
      const raw = window.localStorage.getItem(DRAFT_KEY)
      if (raw) {
        const draft = JSON.parse(raw)
        if (
          draft &&
          typeof draft._savedAt === "number" &&
          Date.now() - draft._savedAt <= DRAFT_TTL_MS
        ) {
          if (draft.activeMode === "motion-control" || draft.activeMode === "images") {
            setActiveMode(draft.activeMode)
          }
          if (typeof draft.imagePrompt === "string") {
            setImagePrompt(draft.imagePrompt.slice(0, 500))
          }
          if (Array.isArray(draft.selectedImageTags)) {
            setSelectedImageTags(draft.selectedImageTags.filter((s) => typeof s === "string"))
          }
          if (typeof draft.selectedImageFilterId === "string" || draft.selectedImageFilterId === null) {
            setSelectedImageFilterId(draft.selectedImageFilterId)
          }
          if (typeof draft.selectedCharacterId === "string" || draft.selectedCharacterId === null) {
            setSelectedCharacterId(draft.selectedCharacterId)
          }
          if (typeof draft.transferPeople === "boolean") {
            setTransferPeople(draft.transferPeople)
          }
          if (draft.selectedMotion && typeof draft.selectedMotion === "object") {
            // Re-resolve to the live MOTION_EXAMPLES entry by id so we always
            // use the current definition (URLs etc) and avoid serving stale
            // motion data from an old draft.
            const restored = MOTION_EXAMPLES.find((m) => m.id === draft.selectedMotion.id)
            if (restored) setSelectedMotion(restored)
          }
          if (typeof draft.motionPrompt === "string") {
            setMotionPrompt(draft.motionPrompt.slice(0, 500))
          }
          if (Array.isArray(draft.selectedMotionTags)) {
            setSelectedMotionTags(draft.selectedMotionTags.filter((s) => typeof s === "string"))
          }
          if (typeof draft.motionAdvancedOpen === "boolean") {
            setMotionAdvancedOpen(draft.motionAdvancedOpen)
          }
          if (draft.motionModelTier === "pro" || draft.motionModelTier === "basic") {
            setMotionModelTier(draft.motionModelTier)
          }
        } else {
          window.localStorage.removeItem(DRAFT_KEY)
        }
      }
    } catch (_) {
      // Corrupt JSON or storage disabled — ignore.
    }
    setDraftHydrated(true)
  }, [])

  useEffect(() => {
    if (!draftHydrated) return
    const DRAFT_KEY = "pinkcatai.dashboard.draft.v1"
    try {
      const draft = {
        _savedAt: Date.now(),
        activeMode,
        imagePrompt,
        selectedImageTags,
        selectedImageFilterId,
        selectedCharacterId,
        transferPeople,
        selectedMotion: selectedMotion ? { id: selectedMotion.id } : null,
        motionPrompt,
        selectedMotionTags,
        motionAdvancedOpen,
        motionModelTier,
      }
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
    } catch (_) {}
  }, [
    draftHydrated,
    activeMode,
    imagePrompt,
    selectedImageTags,
    selectedImageFilterId,
    selectedCharacterId,
    transferPeople,
    selectedMotion,
    motionPrompt,
    selectedMotionTags,
    motionAdvancedOpen,
    motionModelTier,
  ])

  // --- Responsive ---
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener("resize", onResize)
    onResize()
    return () => window.removeEventListener("resize", onResize)
  }, [])

  // --- Hide-from-history persistence (mirrors tryon page) ---
  const storageKey = useMemo(
    () => (userId ? `hiddenGallery:${userId}` : null),
    [userId]
  )

  const getHideKeyForImage = useCallback((img) => {
    return img.originalUrl || img.imageUrl || String(img.id)
  }, [])

  useEffect(() => {
    if (!storageKey) {
      setHiddenKeys(new Set())
      return
    }
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) {
        const arr = JSON.parse(raw)
        if (Array.isArray(arr)) {
          setHiddenKeys(new Set(arr))
          return
        }
      }
    } catch (_) {}
    setHiddenKeys(new Set())
  }, [storageKey])

  const handleHideImage = useCallback(
    (img) => {
      if (!storageKey) return
      const key = getHideKeyForImage(img)
      setHiddenKeys((prev) => {
        const next = new Set(prev)
        next.add(key)
        try {
          localStorage.setItem(storageKey, JSON.stringify(Array.from(next)))
        } catch (_) {}
        return next
      })
      try {
        if (userId && (img.baseImageId || img.id)) {
          const imageDocRef = doc(
            firestore,
            "users",
            userId,
            "video",
            String(img.baseImageId || img.id)
          )
          updateDoc(imageDocRef, { hidden: true })
        }
      } catch (_) {}
    },
    [storageKey, getHideKeyForImage, userId]
  )

  // Stable callbacks for ImageGallery so its React.memo wrapper is effective.
  // Without useCallback these would be a new reference on every keystroke in
  // the prompt textarea, causing the gallery (and every <img>) to re-render
  // and flicker.
  const handleGalleryUsePrompt = useCallback((prompt) => {
    if (!prompt) return
    setImagePrompt(String(prompt).slice(0, 500))
    setSelectedImageTags([])
    setSelectedImageFilterId(null)
    setSelectedCharacterId(null)
    setTransferPeople(false)
  }, [])

  const handleGalleryUseForVideo = useCallback((imageUrl) => {
    if (!imageUrl) return
    setCharacterImageUrl(imageUrl)
    setActiveMode("motion-control")
  }, [])

  const visibleVideoItems = useMemo(() => {
    const videos = (galleryImages || []).filter((it) => it.mediaType === "video")
    if (hiddenKeys.size === 0) return videos
    return videos.filter((img) => !hiddenKeys.has(getHideKeyForImage(img)))
  }, [galleryImages, hiddenKeys, getHideKeyForImage])

  const visibleImageItems = useMemo(() => {
    const images = (galleryImages || []).filter(
      (it) => it.mediaType === "image" || !it.mediaType
    )
    if (hiddenKeys.size === 0) return images
    return images.filter((img) => !hiddenKeys.has(getHideKeyForImage(img)))
  }, [galleryImages, hiddenKeys, getHideKeyForImage])

  // --- Escape closes fullscreen ---
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && fullscreenImage) setFullscreenImage(null)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [fullscreenImage])

  // Prevent the browser from navigating away (opening the file) when a file
  // is accidentally dropped outside one of our drop targets.
  useEffect(() => {
    const stop = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }
    window.addEventListener("dragover", stop)
    window.addEventListener("drop", stop)
    return () => {
      window.removeEventListener("dragover", stop)
      window.removeEventListener("drop", stop)
    }
  }, [])

  const signInWithGoogle = async () => {
    await authGoogleSignIn(setError, auth)
  }

  // --- Character image upload ---
  // Hilfsfunktion: zieht die geparkte Datei und macht den eigentlichen R2-Upload.
  const uploadCharacterFile = async (file, uid) => {
    setCharacterUploading(true)
    try {
      const url = await uploadToR2(file, uid, () => {}, "drafts/character")
      if (!url) throw new Error("Upload failed.")
      setCharacterImageUrl(url)
      // Lokale Blob-Preview wird durch die R2-URL ersetzt.
      setCharacterLocalPreview((prev) => {
        if (prev) {
          try { URL.revokeObjectURL(prev) } catch (_) {}
        }
        return null
      })
    } catch (err) {
      setError(`Character upload failed: ${err?.message || err}`)
    } finally {
      setCharacterUploading(false)
    }
  }

  const processCharacterFile = async (file) => {
    if (!file) return
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.")
      return
    }
    const sizeMB = file.size / (1024 * 1024)
    if (sizeMB > 25) {
      setError("Character image must be ≤ 25 MB.")
      return
    }
    setError("")

    // Sofortige lokale Vorschau – egal ob eingeloggt oder nicht. So sieht der
    // User auf dem Smartphone direkt sein Bild, auch wenn der Login-Redirect
    // gleich noch passiert.
    try {
      const localUrl = URL.createObjectURL(file)
      setCharacterLocalPreview((prev) => {
        if (prev) {
          try { URL.revokeObjectURL(prev) } catch (_) {}
        }
        return localUrl
      })
      setCharacterImageUrl(null)
    } catch (_) {}

    if (!userId) {
      // Kein automatischer Login – der wird erst beim Generate-Klick getriggert.
      // Wir parken die Datei nur; der Upload läuft automatisch, sobald `userId`
      // verfügbar ist (siehe useEffect weiter unten).
      pendingCharacterFileRef.current = file
      return
    }

    pendingCharacterFileRef.current = null
    await uploadCharacterFile(file, userId)
  }

  const handleCharacterFile = (e) => {
    const file = e.target.files?.[0]
    e.target.value = null
    processCharacterFile(file)
  }

  const handleCharacterDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setCharacterDragOver(false)
    const file = e.dataTransfer?.files?.[0]
    if (file) processCharacterFile(file)
  }

  // --- Orientation image upload (Images mode) ---
  const uploadOrientationFile = async (file, uid) => {
    setOrientationUploading(true)
    try {
      const url = await uploadToR2(file, uid, () => {}, "drafts/orientation")
      if (!url) throw new Error("Upload failed.")
      updateOrientation(url)
      setOrientationLocalPreview((prev) => {
        if (prev) {
          try { URL.revokeObjectURL(prev) } catch (_) {}
        }
        return null
      })
    } catch (err) {
      setError(`Reference image upload failed: ${err?.message || err}`)
    } finally {
      setOrientationUploading(false)
    }
  }

  const processOrientationFile = async (file) => {
    if (!file) return
    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.")
      return
    }
    const sizeMB = file.size / (1024 * 1024)
    if (sizeMB > 25) {
      setError("Reference image must be ≤ 25 MB.")
      return
    }
    setError("")

    try {
      const localUrl = URL.createObjectURL(file)
      setOrientationLocalPreview((prev) => {
        if (prev) {
          try { URL.revokeObjectURL(prev) } catch (_) {}
        }
        return localUrl
      })
    } catch (_) {}

    if (!userId) {
      // Kein automatischer Login – Login erst bei Generate. Datei wird geparkt
      // und nach erfolgreichem Login automatisch hochgeladen.
      pendingOrientationFileRef.current = file
      return
    }

    pendingOrientationFileRef.current = null
    await uploadOrientationFile(file, userId)
  }

  const handleOrientationFile = (e) => {
    const file = e.target.files?.[0]
    e.target.value = null
    processOrientationFile(file)
  }

  const handleOrientationDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setOrientationDragOver(false)
    const file = e.dataTransfer?.files?.[0]
    if (file) processOrientationFile(file)
  }

  // Sobald `userId` gesetzt wird (z.B. nach einem Mobile-Redirect-Login),
  // holen wir vorab geparkte Uploads automatisch nach. Beide Refs werden
  // dabei sofort geleert, damit derselbe Upload nicht doppelt läuft.
  useEffect(() => {
    if (!userId) return
    const pendingChar = pendingCharacterFileRef.current
    if (pendingChar) {
      pendingCharacterFileRef.current = null
      uploadCharacterFile(pendingChar, userId)
    }
    const pendingOri = pendingOrientationFileRef.current
    if (pendingOri) {
      pendingOrientationFileRef.current = null
      uploadOrientationFile(pendingOri, userId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  // Blob-URLs sauber beim Unmount aufräumen.
  useEffect(() => {
    return () => {
      if (characterLocalPreview) {
        try { URL.revokeObjectURL(characterLocalPreview) } catch (_) {}
      }
      if (orientationLocalPreview) {
        try { URL.revokeObjectURL(orientationLocalPreview) } catch (_) {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Centralized orientation toggling — also injects/removes the
  // "like in image" hint into the prompt, placed right after the viral
  // character (if one is selected), otherwise at the start of the prompt.
  const updateOrientation = (newUrl) => {
    const phrase = "like in image"
    const characters = ALL_CHARACTER_PHRASES

    let text = imagePrompt
    if (text.includes(`, ${phrase}, `))
      text = text.replace(`, ${phrase}, `, ", ")
    else if (text.includes(`, ${phrase}`))
      text = text.replace(`, ${phrase}`, "")
    else if (text.includes(`${phrase}, `))
      text = text.replace(`${phrase}, `, "")
    else text = text.replace(phrase, "")
    text = text
      .trim()
      .replace(/^,\s*/, "")
      .replace(/,\s*$/, "")

    setOrientationImageUrl(newUrl)

    if (!newUrl) {
      setImagePrompt(text.slice(0, 500))
      return
    }

    let next = text
    let inserted = false
    for (const c of characters) {
      if (text.startsWith(`${c}, `)) {
        next = `${c}, ${phrase}, ${text.slice(`${c}, `.length)}`
        inserted = true
        break
      }
      if (text === c) {
        next = `${c}, ${phrase}`
        inserted = true
        break
      }
    }
    if (!inserted) {
      next = text ? `${phrase}, ${text}` : phrase
    }
    setImagePrompt(next.slice(0, 500))
  }

  const applyImportedMotion = (url, label, source) => {
    setCustomMotionUrl(url)
    setCustomMotionLabel(label || "")
    setCustomMotionSource(source)
    setSelectedMotion(null)
    setTiktokPanelOpen(false)

    // Persistiere den Import als Eintrag in der Motion-Galerie des Users,
    // damit er später wieder ausgewählt werden kann. De-Dup über die URL —
    // ein erneutes Klicken auf einen bereits gespeicherten Tile darf keinen
    // doppelten Eintrag erzeugen.
    if (!userId || !url) return
    const alreadySaved = savedMotionVideos.some((s) => s.url === url)
    if (alreadySaved) return
    addDoc(collection(firestore, "users", userId, "motionVideos"), {
      url,
      name: label || "",
      source: source === "tiktok" ? "tiktok" : "upload",
      createdAt: serverTimestamp(),
    }).catch((err) => {
      console.warn("[dashboard] failed to save motion video to Firestore:", err)
    })
  }

  const handleSelectSavedMotion = (savedItem) => {
    if (!savedItem || !savedItem.url) return
    setCustomMotionUrl(savedItem.url)
    setCustomMotionLabel(savedItem.name || "")
    setCustomMotionSource(savedItem.source || "upload")
    setSelectedMotion(null)
    setTiktokPanelOpen(false)
  }

  const handleDeleteSavedMotion = async (savedItem) => {
    if (!userId || !savedItem?.id) return
    if (customMotionUrl === savedItem.url) {
      setCustomMotionUrl(null)
      setCustomMotionLabel("")
      setCustomMotionSource(null)
    }
    try {
      await deleteDoc(
        doc(firestore, "users", userId, "motionVideos", savedItem.id),
      )
    } catch (err) {
      console.warn("[dashboard] failed to delete saved motion video:", err)
    }
  }

  const openTikTokPanel = () => {
    setTiktokError("")
    setTiktokPanelOpen(true)
    setTimeout(() => tiktokInputRef.current?.focus(), 30)
  }

  // --- Inline upload-tile click ---
  const trimMotionVideoOnServer = async (videoUrl) => {
    const idToken = await auth.currentUser.getIdToken()
    // Direct Cloud Run URL — siehe Kommentar bei TRIM_VIDEO_TO_MAX_URL
    // (Hosting würde nach 60 s mit 502 abbrechen).
    const res = await fetch(TRIM_VIDEO_TO_MAX_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        videoUrl,
        maxSeconds: MOTION_VIDEO_TRIM_SECONDS,
      }),
    })
    let data = null
    try {
      data = await res.json()
    } catch (_) {}
    if (!res.ok || !data?.ok) {
      throw new Error(data?.error || `Trim failed (HTTP ${res.status}).`)
    }
    return data.videoUrl
  }

  const processMotionVideoFile = async (file) => {
    if (!file) return
    setUploadError("")
    if (!file.type && !/\.(mp4|mov|webm|mkv|avi)$/i.test(file.name || "")) {
      setUploadError("Please drop a video file (MP4, MOV, WebM, MKV, or AVI).")
      return
    }
    if (file.type && !file.type.startsWith("video/")) {
      setUploadError("Please drop a video file (MP4, MOV, WebM, MKV, or AVI).")
      return
    }
    if (!userId) {
      signInWithGoogle()
      return
    }
    const sizeMB = file.size / (1024 * 1024)
    if (sizeMB > 100) {
      setUploadError("Motion video must be ≤ 100 MB.")
      return
    }
    setUploadBusy(true)
    setUploadStatus("Uploading…")
    try {
      // Read duration locally before upload so we know whether to trim.
      const localDuration = await getVideoDurationFromFile(file)

      const url = await uploadToR2(file, userId, () => {}, "drafts/video")
      if (!url) throw new Error("Upload failed.")

      let finalUrl = url
      if (localDuration && localDuration > MOTION_VIDEO_MAX_SECONDS) {
        setUploadStatus(`Trimming to ${MOTION_VIDEO_TRIM_SECONDS}s…`)
        try {
          finalUrl = await trimMotionVideoOnServer(url)
        } catch (trimErr) {
          console.warn("Server trim failed, keeping original:", trimErr)
          setUploadError(
            `Couldn't auto-trim long video (${trimErr?.message || trimErr}). ` +
              `Using full clip — generation may fail if it's longer than ` +
              `${MOTION_VIDEO_MAX_SECONDS}s.`,
          )
        }
      }

      applyImportedMotion(finalUrl, file.name, "upload")
    } catch (err) {
      setUploadError(`Upload failed: ${err?.message || err}`)
    } finally {
      setUploadBusy(false)
      setUploadStatus("")
    }
  }

  const handleModalUploadFile = (e) => {
    const file = e.target.files?.[0]
    e.target.value = null
    processMotionVideoFile(file)
  }

  const handleMotionDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setMotionDragOver(false)
    const file = e.dataTransfer?.files?.[0]
    if (file) processMotionVideoFile(file)
  }

  // --- Modal: TikTok import tab ---
  const handleTikTokImport = async () => {
    setTiktokError("")
    const url = (tiktokUrl || "").trim()
    if (!url) {
      setTiktokError("Please paste a TikTok link.")
      return
    }
    if (!/tiktok\.com\//i.test(url)) {
      setTiktokError("That doesn't look like a TikTok link.")
      return
    }
    if (!userId) {
      signInWithGoogle()
      return
    }
    setTiktokBusy(true)
    try {
      const idToken = await auth.currentUser.getIdToken()
      // Direct Cloud Run URL statt Hosting-Rewrite, weil yt-dlp + ffmpeg-
      // Trim eines längeren TikTok-Clips öfter > 60 s braucht und Firebase
      // Hosting dann mit HTTP 502 abbricht (siehe Kommentar bei
      // EXTRACT_TIKTOK_VIDEO_URL ganz oben). Die Funktion setzt permissive
      // CORS-Header, daher klappt der Cross-Origin-Call von SPA + localhost.
      const res = await fetch(EXTRACT_TIKTOK_VIDEO_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ url }),
      })
      let data = null
      try {
        data = await res.json()
      } catch (_) {}
      if (!res.ok || !data?.ok) {
        setTiktokError(data?.error || `Import failed (HTTP ${res.status}).`)
        return
      }
      const label = data.title || "TikTok video"
      applyImportedMotion(data.videoUrl, label, "tiktok")
      setTiktokUrl("")
    } catch (err) {
      // Häufiger Fall: Browser bricht die lange fetch-Connection ab
      // ("Load failed" in Safari, "Failed to fetch" in Chrome) — die
      // Function selbst läuft aber serverseitig durch und schreibt das
      // Firestore-Doc, das per onSnapshot automatisch in der Galerie
      // landet. Wir teilen dem User das mit, statt einen harten Fehler
      // anzuzeigen, und schließen das Panel optimistisch.
      const msg = String(err?.message || err || "")
      const looksLikeNetworkAbort =
        /load failed|failed to fetch|networkerror|the network connection was lost/i.test(
          msg,
        )
      if (looksLikeNetworkAbort) {
        setTiktokError(
          "Connection dropped while waiting for TikTok server. " +
            "Your video should appear in the gallery in a few seconds — " +
            "no need to retry.",
        )
        setTiktokUrl("")
      } else {
        setTiktokError(`Import failed: ${msg || err}`)
      }
    } finally {
      setTiktokBusy(false)
    }
  }

  // --- Generate ---
  const handleGenerate = async () => {
    setError("")

    if (!userId) {
      signInWithGoogle()
      return
    }
    if (!characterImageUrl) {
      setError("Please add a character image first.")
      return
    }
    const motionVideoUrl = customMotionUrl || selectedMotion?.motionVideoUrl
    if (!motionVideoUrl) {
      setError("Please select or import a motion video.")
      return
    }
    const effectiveVideoCost =
      motionModelTier === "basic" ? VIDEO_CREDIT_COST_BASIC : VIDEO_CREDIT_COST
    if ((balance ?? 0) < effectiveVideoCost) {
      navigate("/pricing")
      return
    }

    setIsGenerating(true)
    try {

      const videoCollectionRef = collection(firestore, "users", userId, "video")
      const videoDocRef = doc(videoCollectionRef)

      const batch = writeBatch(firestore)
      batch.set(videoDocRef, {
        imageUrl: characterImageUrl,
        motionVideoUrl,
        characterOrientation: "video",
        prompt: (motionPrompt || "").trim(),
        modelTier: motionModelTier,
        creditCost: effectiveVideoCost,
        generationStatus: "pending",
        createdAt: serverTimestamp(),
        processedAt: null,
        source: "dashboard",
      })
      await batch.commit()
      console.log("[dashboard] video doc created", {
        userId,
        videoId: videoDocRef.id,
      })
      setSubmittedToast({ kind: "video" })
    } catch (err) {
      const code = err?.code ? ` (${err.code})` : ""
      setError(`Video generation failed${code}: ${err?.message || err}`)
    } finally {
      setIsGenerating(false)
    }
  }

  // --- Generate (Images mode → fal nano-banana-pro text-to-image) ---
  const handleGenerateImage = async () => {
    setError("")

    if (!userId) {
      signInWithGoogle()
      return
    }
    const promptText = (imagePrompt || "").trim()
    if (!promptText) {
      setError("Please describe your character first.")
      return
    }
    if ((balance ?? 0) < IMAGE_CREDIT_COST) {
      navigate("/pricing")
      return
    }

    setIsGenerating(true)
    try {
      const imageCollectionRef = collection(firestore, "users", userId, "image")
      const imageDocRef = doc(imageCollectionRef)

      const payload = {
        prompt: promptText,
        aspect_ratio: "9:16",
        size: "2K",
        output_format: "jpeg",
        creditCost: IMAGE_CREDIT_COST,
        generationStatus: "pending",
        createdAt: serverTimestamp(),
        processedAt: null,
        source: "dashboard",
      }
      if (orientationImageUrl) {
        payload.imageUrl = orientationImageUrl
      } else {
        payload.generationMode = "text-to-image"
      }

      const batch = writeBatch(firestore)
      batch.set(imageDocRef, payload)
      await batch.commit()
      console.log("[dashboard] image doc created", {
        userId,
        imageId: imageDocRef.id,
      })
      setSubmittedToast({ kind: "image" })
    } catch (err) {
      const code = err?.code ? ` (${err.code})` : ""
      setError(`Image generation failed${code}: ${err?.message || err}`)
    } finally {
      setIsGenerating(false)
    }
  }

  // ============================================================
  // STYLES
  // ============================================================
  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 100px)",
    height: isMobile ? "auto" : "calc(100vh - 100px)",
    fontFamily: '"Inter", sans-serif',
    backgroundColor: "#ffffff",
    color: TEXT_DARK,
    boxSizing: "border-box",
  }

  const topBarStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: isMobile ? "12px 16px 8px" : "16px 24px 8px",
    flexWrap: "wrap",
    gap: "12px",
    flexShrink: 0,
  }

  const layoutStyle = {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "16px" : "20px",
    padding: isMobile ? "0 16px 16px" : "0 24px 24px",
    flex: 1,
    minHeight: 0,
    boxSizing: "border-box",
  }

  const leftColStyle = {
    flex: isMobile ? "none" : "0 0 420px",
    width: isMobile ? "100%" : "420px",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#ffffff",
    border: `1px solid ${PINK_BORDER}`,
    borderRadius: "16px",
    boxShadow: "0 4px 14px rgba(255, 79, 184, 0.08)",
    overflow: "hidden",
    minHeight: 0,
  }

  const leftScrollStyle = {
    flex: 1,
    overflowY: isMobile ? "visible" : "auto",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    minHeight: 0,
  }

  const leftFooterStyle = {
    flexShrink: 0,
    padding: "12px 16px",
    borderTop: `1px solid ${PINK_BORDER}`,
    background: "#ffffff",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  }

  const rightColStyle = {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#ffffff",
    border: `1px solid ${PINK_BORDER}`,
    borderRadius: "16px",
    boxShadow: "0 4px 14px rgba(255, 79, 184, 0.06)",
    overflow: "hidden",
    minHeight: 0,
  }

  const rightHeaderStyle = {
    padding: "14px 18px",
    borderBottom: `1px solid ${PINK_BORDER}`,
    fontSize: "13px",
    fontWeight: 700,
    color: TEXT_MUTED,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    flexShrink: 0,
  }

  const rightBodyStyle = {
    flex: 1,
    overflowY: isMobile ? "visible" : "auto",
    padding: isMobile ? "12px" : "16px",
    minHeight: 0,
  }

  const sectionLabelStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "13px",
    fontWeight: 700,
    color: TEXT_MUTED,
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    marginBottom: "8px",
  }

  // ============================================================
  // PIECES
  // ============================================================

  const CreditPill = (
    <button
      type="button"
      onClick={() => navigate("/pricing")}
      title="Manage credits"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 14px",
        borderRadius: "999px",
        border: `1px solid ${PINK_BORDER}`,
        background: PINK_BG_SOFT,
        color: TEXT_DARK,
        fontWeight: 600,
        fontSize: "13px",
        cursor: "pointer",
        boxShadow: "0 2px 6px rgba(255, 79, 184, 0.10)",
      }}
    >
      <CreditIcon size={16} />
      <span>{userId ? balance.toLocaleString() : "—"}</span>
      <span style={{ color: TEXT_MUTED, fontWeight: 500 }}>credits</span>
    </button>
  )

  const ModeSelector = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
      }}
    >
      {MODES.map((m) => {
        const isActive = m.id === activeMode
        return (
          <button
            key={m.id}
            type="button"
            disabled={!m.available}
            onClick={() => m.available && setActiveMode(m.id)}
            style={{
              padding: "10px 18px",
              borderRadius: "999px",
              border: isActive ? "none" : `1px solid ${PINK_BORDER}`,
              cursor: m.available ? "pointer" : "not-allowed",
              fontSize: isMobile ? "15px" : "16px",
              fontWeight: 700,
              letterSpacing: "0.2px",
              color: isActive ? "#fff" : TEXT_MUTED,
              background: isActive
                ? `linear-gradient(135deg, ${PINK} 0%, #ff85cc 100%)`
                : "#ffffff",
              boxShadow: isActive
                ? "0 6px 16px rgba(255, 79, 184, 0.35)"
                : "none",
              opacity: m.available ? 1 : 0.5,
            }}
          >
            {m.label}
          </button>
        )
      })}
    </div>
  )

  const InfoTooltip = (
    <span
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setShowCharTooltip(true)}
      onMouseLeave={() => setShowCharTooltip(false)}
      onClick={() => setShowCharTooltip((v) => !v)}
    >
      <span
        aria-label="What is this?"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "16px",
          height: "16px",
          borderRadius: "50%",
          border: `1px solid ${PINK_BORDER}`,
          color: TEXT_MUTED,
          fontSize: "11px",
          fontWeight: 700,
          fontStyle: "italic",
          fontFamily: "Georgia, serif",
          cursor: "help",
          background: "#fff",
        }}
      >
        i
      </span>
      {showCharTooltip && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1a1a1a",
            color: "#fff",
            padding: "8px 12px",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: 500,
            lineHeight: 1.4,
            letterSpacing: 0,
            textTransform: "none",
            width: "240px",
            zIndex: 50,
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
          }}
        >
          Upload an image of yourself, a friend, or anything you find online.
          This image will be the character you see in the generated video.
        </span>
      )}
    </span>
  )

  // Bevorzuge die schon hochgeladene R2-URL; falls noch nicht hochgeladen,
  // zeige die lokale Blob-Vorschau (wichtig auf Mobile, wo der Login-Redirect
  // den Upload kurz verzögern kann).
  const characterDisplayUrl = characterImageUrl || characterLocalPreview
  const CharacterUpload = (
    <button
      type="button"
      onClick={() => characterInputRef.current?.click()}
      disabled={characterUploading}
      onDragEnter={(e) => {
        e.preventDefault()
        e.stopPropagation()
        if (!characterUploading) setCharacterDragOver(true)
      }}
      onDragOver={(e) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"
        if (!characterUploading && !characterDragOver) setCharacterDragOver(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.currentTarget.contains(e.relatedTarget)) return
        setCharacterDragOver(false)
      }}
      onDrop={handleCharacterDrop}
      style={{
        position: "relative",
        width: "100%",
        minHeight: characterDisplayUrl ? "auto" : "120px",
        padding: characterDisplayUrl ? "0" : "20px",
        borderRadius: "14px",
        border: characterDragOver
          ? `2px dashed ${PINK_DEEP}`
          : characterDisplayUrl
          ? `2px solid ${PINK_DEEP}`
          : `2px dashed ${PINK_BORDER}`,
        background: characterDragOver
          ? "#fce4ec"
          : characterDisplayUrl
          ? "#ffffff"
          : `linear-gradient(135deg, ${PINK_BG_SOFT} 0%, #fce4ec 100%)`,
        cursor: characterUploading ? "wait" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "8px",
        overflow: "hidden",
        transition: "transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease, border-color 0.15s ease",
      }}
      onMouseEnter={(e) => {
        if (characterUploading) return
        e.currentTarget.style.boxShadow =
          "0 6px 18px rgba(255, 79, 184, 0.20)"
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none"
      }}
    >
      {characterDisplayUrl ? (
        <>
          <img
            src={characterDisplayUrl}
            alt="Character"
            style={{
              width: "100%",
              maxHeight: "320px",
              objectFit: "contain",
              display: "block",
              borderRadius: "12px",
              background: PINK_BG_SOFT,
            }}
          />
          <span
            onClick={(e) => {
              e.stopPropagation()
              setCharacterImageUrl(null)
              setCharacterLocalPreview((prev) => {
                if (prev) {
                  try { URL.revokeObjectURL(prev) } catch (_) {}
                }
                return null
              })
              pendingCharacterFileRef.current = null
            }}
            title="Remove"
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              width: "26px",
              height: "26px",
              borderRadius: "50%",
              background: "rgba(0,0,0,0.55)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            ×
          </span>
          <span
            style={{
              position: "absolute",
              bottom: "10px",
              left: "10px",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.3px",
              padding: "4px 10px",
              borderRadius: "999px",
              background: "rgba(255,255,255,0.92)",
              color: PINK_DEEP,
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            }}
          >
            {characterUploading ? "Uploading…" : "Change"}
          </span>
        </>
      ) : (
        <>
          <div
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${PINK} 0%, #ff85cc 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 6px 16px rgba(255, 79, 184, 0.35)",
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <span
            style={{
              fontSize: "15px",
              fontWeight: 700,
              color: TEXT_DARK,
            }}
          >
            {characterUploading ? "Uploading…" : "Upload Character Image"}
          </span>
          <span style={{ fontSize: "12px", color: TEXT_MUTED }}>
            JPG, PNG, or WebP · up to 25 MB
          </span>
        </>
      )}
      <input
        ref={characterInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleCharacterFile}
      />
    </button>
  )

  const MotionTile = ({
    tile,
    savedItem,
    variant = "example", // 'upload' | 'tiktok' | 'example' | 'saved'
    isActive = false,
    busy = false,
    onClick,
    onDelete,
  }) => {
    const isAdd = variant === "upload" || variant === "tiktok"
    const isUpload = variant === "upload"
    const isTikTok = variant === "tiktok"
    const isSaved = variant === "saved"
    const showDragOver = isUpload && motionDragOver

    // Background-/Style-Helper für Saved-Tiles (Source bestimmt das Farbschema).
    const savedIsTikTok = isSaved && savedItem?.source === "tiktok"

    const tileStyle = {
      position: "relative",
      borderRadius: "14px",
      overflow: "hidden",
      cursor: busy ? "wait" : "pointer",
      aspectRatio: "3 / 4",
      width: "100%",
      border: showDragOver
        ? `2px dashed ${PINK}`
        : isActive
        ? `2px solid ${PINK}`
        : `1px solid ${PINK_BORDER}`,
      boxShadow: isActive || showDragOver
        ? "0 6px 16px rgba(255, 79, 184, 0.35)"
        : "0 2px 6px rgba(74, 26, 44, 0.06)",
      background:
        variant === "upload"
          ? `linear-gradient(135deg, #ffe4ef 0%, #ffd2e6 50%, #ffc1dc 100%)`
          : variant === "tiktok"
          ? `linear-gradient(135deg, #1a1a1a 0%, #221327 100%)`
          : "#000",
      transform: showDragOver ? "scale(1.02)" : "none",
      transition: "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
    }

    const addLabel = isUpload ? "Upload" : "TikTok link"
    const addLabelColor = isUpload ? PINK_DEEP : "#ffffff"

    const dragHandlers = isUpload
      ? {
          onDragEnter: (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (!busy) setMotionDragOver(true)
          },
          onDragOver: (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"
            if (!busy && !motionDragOver) setMotionDragOver(true)
          },
          onDragLeave: (e) => {
            e.preventDefault()
            e.stopPropagation()
            if (e.currentTarget.contains(e.relatedTarget)) return
            setMotionDragOver(false)
          },
          onDrop: handleMotionDrop,
        }
      : {}

    return (
      <div style={tileStyle} onClick={busy ? undefined : onClick} {...dragHandlers}>
        {isAdd ? (
          <>
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -54%)",
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isTikTok
                  ? "0 4px 12px rgba(0, 0, 0, 0.40)"
                  : "0 4px 12px rgba(255, 79, 184, 0.30)",
              }}
            >
              {busy ? (
                <span
                  aria-hidden="true"
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "50%",
                    border: "2px solid rgba(217, 106, 147, 0.25)",
                    borderTopColor: "#d96a93",
                    animation: "pinkcatai-spin 0.8s linear infinite",
                    display: "inline-block",
                  }}
                />
              ) : isTikTok ? (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1a1a1a"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 17V5l11-2v12" />
                  <circle cx="6" cy="17" r="3" />
                  <circle cx="17" cy="15" r="3" />
                </svg>
              ) : (
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={PINK}
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              )}
              {!busy && (
                <span
                  style={{
                    position: "absolute",
                    bottom: "-3px",
                    right: "-3px",
                    width: "14px",
                    height: "14px",
                    borderRadius: "50%",
                    background: isTikTok ? "#FE2C55" : PINK,
                    color: "#fff",
                    fontSize: "10px",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid #fff",
                    lineHeight: 1,
                  }}
                >
                  +
                </span>
              )}
            </div>
            <span
              style={{
                position: "absolute",
                left: "6px",
                right: "6px",
                bottom: "8px",
                fontSize: "11px",
                fontWeight: 700,
                color: addLabelColor,
                textAlign: "center",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textShadow: isTikTok ? "0 1px 2px rgba(0,0,0,0.6)" : "none",
              }}
              title={addLabel}
            >
              {addLabel}
            </span>
          </>
        ) : isSaved && savedItem ? (
          <>
            <video
              src={`${savedItem.url}#t=0.1`}
              muted
              playsInline
              preload="metadata"
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.10) 45%, rgba(0,0,0,0.78) 100%)",
                pointerEvents: "none",
              }}
            />
            <span
              style={{
                position: "absolute",
                top: "6px",
                left: "6px",
                fontSize: "9px",
                fontWeight: 700,
                color: "#fff",
                padding: "2px 6px",
                borderRadius: "999px",
                background: savedIsTikTok
                  ? "linear-gradient(135deg, #25F4EE 0%, #FE2C55 100%)"
                  : `linear-gradient(135deg, ${PINK} 0%, #ff85cc 100%)`,
                letterSpacing: "0.4px",
                textTransform: "uppercase",
                boxShadow: "0 2px 6px rgba(0,0,0,0.20)",
              }}
            >
              {savedIsTikTok ? "TikTok" : "Yours"}
            </span>
            {onDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete()
                }}
                title="Remove from gallery"
                aria-label="Remove from gallery"
                style={{
                  position: "absolute",
                  top: "6px",
                  right: "6px",
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  background: "rgba(0,0,0,0.55)",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "13px",
                  lineHeight: 1,
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                }}
              >
                ×
              </button>
            )}
            <span
              style={{
                position: "absolute",
                left: "8px",
                right: "8px",
                bottom: "8px",
                fontSize: "11px",
                fontWeight: 700,
                color: "#fff",
                textAlign: "center",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textShadow: "0 1px 2px rgba(0,0,0,0.6)",
              }}
              title={savedItem.name || (savedIsTikTok ? "TikTok video" : "Your video")}
            >
              {savedItem.name || (savedIsTikTok ? "TikTok video" : "Your video")}
            </span>
          </>
        ) : (
          <>
            <img
              src={tile.previewImage}
              alt={tile.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.65) 100%)",
              }}
            />
            {tile.badge && (
              <span
                style={{
                  position: "absolute",
                  top: "8px",
                  left: "8px",
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "#fff",
                  padding: "3px 8px",
                  borderRadius: "999px",
                  background: `linear-gradient(135deg, ${PINK} 0%, #ff85cc 100%)`,
                  letterSpacing: "0.4px",
                  textTransform: "uppercase",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.20)",
                }}
              >
                {tile.badge}
              </span>
            )}
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.92)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill={PINK}
                style={{ marginLeft: "2px" }}
              >
                <polygon points="6 4 20 12 6 20" />
              </svg>
            </div>
            <span
              style={{
                position: "absolute",
                left: "10px",
                right: "10px",
                bottom: "8px",
                fontSize: "12px",
                fontWeight: 700,
                color: "#fff",
                textShadow: "0 1px 2px rgba(0,0,0,0.5)",
              }}
            >
              {tile.name}
            </span>
          </>
        )}
        {isActive && (
          <span
            style={{
              position: "absolute",
              // Bei Saved-Tiles ist oben-rechts der Delete-Button → Häkchen
              // dorthin verschieben, wo es nicht überlappt.
              ...(isSaved
                ? { bottom: "8px", right: "8px" }
                : { top: "8px", right: "8px" }),
              width: "22px",
              height: "22px",
              borderRadius: "50%",
              background: PINK,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
        )}
      </div>
    )
  }

  const motionGridStyle = {
    display: "grid",
    gridTemplateColumns: isMobile
      ? "repeat(2, minmax(0, 1fr))"
      : "repeat(4, minmax(0, 1fr))",
    gap: "10px",
  }

  const imagesModeActive = activeMode === "images"
  const currentVideoCreditCost =
    motionModelTier === "basic" ? VIDEO_CREDIT_COST_BASIC : VIDEO_CREDIT_COST
  const currentCreditCost = imagesModeActive
    ? IMAGE_CREDIT_COST
    : currentVideoCreditCost
  const generateDisabled =
    isGenerating || characterUploading || orientationUploading

  const GenerateButton = (
    <button
      type="button"
      disabled={generateDisabled}
      onClick={imagesModeActive ? handleGenerateImage : handleGenerate}
      style={{
        width: "100%",
        padding: "14px 18px",
        borderRadius: "14px",
        border: "none",
        cursor: isGenerating ? "wait" : "pointer",
        color: "#ffffff",
        fontWeight: 700,
        fontSize: "15px",
        letterSpacing: "0.3px",
        background: isGenerating
          ? "linear-gradient(135deg, #c79bb1 0%, #d8a8c2 50%, #e0b598 100%)"
          : `linear-gradient(135deg, ${PINK} 0%, #ff85cc 50%, #ffb15a 100%)`,
        boxShadow: isGenerating
          ? "0 4px 12px rgba(255, 79, 184, 0.20)"
          : "0 6px 18px rgba(255, 79, 184, 0.45)",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        opacity: isGenerating ? 0.85 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
      }}
      onMouseEnter={(e) => {
        if (generateDisabled) return
        e.currentTarget.style.transform = "translateY(-1px)"
        e.currentTarget.style.boxShadow =
          "0 8px 22px rgba(255, 79, 184, 0.55)"
      }}
      onMouseLeave={(e) => {
        if (generateDisabled) return
        e.currentTarget.style.transform = "translateY(0)"
        e.currentTarget.style.boxShadow =
          "0 6px 18px rgba(255, 79, 184, 0.45)"
      }}
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "4px 10px",
          background: "rgba(255,255,255,0.18)",
          borderRadius: "999px",
          fontSize: "12px",
          fontWeight: 700,
        }}
      >
        <CreditIcon size={14} />
        {currentCreditCost}
      </span>
      <span>{isGenerating ? "Generating…" : "Generate"}</span>
    </button>
  )

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={pageStyle}>
      <style>{`
        @keyframes pinkcatai-spin { to { transform: rotate(360deg); } }
        @keyframes pinkcatai-toast-in {
          from { opacity: 0; transform: translate(-50%, -20px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
        .pinkcatai-scroll {
          scrollbar-width: thin;
          scrollbar-color: ${PINK_DEEP} ${PINK_BG_SOFT};
        }
        .pinkcatai-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
        .pinkcatai-scroll::-webkit-scrollbar-track { background: ${PINK_BG_SOFT}; border-radius: 8px; }
        .pinkcatai-scroll::-webkit-scrollbar-thumb { background: ${PINK_DEEP}; border-radius: 8px; border: 2px solid ${PINK_BG_SOFT}; }
        .pinkcatai-scroll::-webkit-scrollbar-thumb:hover { background: ${PINK}; }
      `}</style>
      {paymentSuccess && (
        <div
          role="alert"
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "linear-gradient(135deg, #ff4fb8 0%, #ff85cc 100%)",
            color: "#ffffff",
            padding: isMobile ? "16px 18px" : "16px 28px",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(255, 79, 184, 0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: "15px",
            width: isMobile ? "calc(100vw - 16px)" : "auto",
            maxWidth: isMobile ? "calc(100vw - 16px)" : "92vw",
            boxSizing: "border-box",
            animation: "pinkcatai-toast-in 0.4s ease-out",
          }}
        >
          <span style={{ fontSize: "22px" }}>🎉</span>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
            <span>Welcome to Pinkcatai!</span>
            <span style={{ fontWeight: 400, fontSize: "13px", opacity: 0.95 }}>
              Your subscription is active — credits will appear shortly.
            </span>
          </div>
          <button
            onClick={() => setPaymentSuccess(false)}
            aria-label="Dismiss"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "#fff",
              borderRadius: "50%",
              width: "26px",
              height: "26px",
              cursor: "pointer",
              fontSize: "16px",
              lineHeight: 1,
              marginLeft: "4px",
            }}
          >
            ×
          </button>
        </div>
      )}
      {submittedToast && (
        <div
          role="status"
          style={{
            position: "fixed",
            top: paymentSuccess ? "92px" : "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "linear-gradient(135deg, #ff4fb8 0%, #ff85cc 100%)",
            color: "#ffffff",
            padding: isMobile ? "12px 16px" : "12px 22px",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(255, 79, 184, 0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: "14px",
            width: isMobile ? "calc(100vw - 16px)" : "auto",
            maxWidth: isMobile ? "calc(100vw - 16px)" : "92vw",
            boxSizing: "border-box",
            animation: "pinkcatai-toast-in 0.4s ease-out",
          }}
        >
          <span style={{ fontSize: "20px" }}>✨</span>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
            <span>
              {submittedToast.kind === "video"
                ? "Generating your video"
                : "Generating your character"}
            </span>
            <span style={{ fontWeight: 400, fontSize: "12px", opacity: 0.95 }}>
              {submittedToast.kind === "video"
                ? "It will appear in the right panel in a few minutes."
                : "It will appear in the right panel shortly."}
            </span>
          </div>
          <button
            onClick={() => setSubmittedToast(null)}
            aria-label="Dismiss"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "#fff",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              cursor: "pointer",
              fontSize: "14px",
              lineHeight: 1,
              marginLeft: "4px",
            }}
          >
            ×
          </button>
        </div>
      )}
      <div style={topBarStyle}>
        {ModeSelector}
        {CreditPill}
      </div>

      <div style={layoutStyle}>
        {/* LEFT: Controls */}
        <div style={leftColStyle}>
          <div style={leftScrollStyle}>
            {/* Character Image — only in Motion Control mode */}
            {activeMode === "motion-control" && (
              <div>
                <div style={sectionLabelStyle}>
                  <span>Add Character Image</span>
                  {InfoTooltip}
                </div>
                {CharacterUpload}

                {/* "or" divider */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    margin: "8px 0 6px",
                  }}
                >
                  <div style={{ flex: 1, height: "1px", background: PINK_BORDER }} />
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: 600,
                      color: TEXT_MUTED,
                      opacity: 0.8,
                      letterSpacing: "0.5px",
                      textTransform: "uppercase",
                    }}
                  >
                    or
                  </span>
                  <div style={{ flex: 1, height: "1px", background: PINK_BORDER }} />
                </div>

                {/* Example gallery — full-width, vertically scrollable */}
                <div
                  className="pinkcatai-scroll"
                  style={{
                    width: "100%",
                    borderRadius: "10px",
                    border: `1px solid ${PINK_BORDER}`,
                    background: PINK_BG_SOFT,
                    padding: "6px",
                    height: "132px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    boxSizing: "border-box",
                    WebkitOverflowScrolling: "touch",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                      gap: "6px",
                    }}
                  >
                    {/* First tile: Create your own character — switches to images mode */}
                    <button
                      type="button"
                      onClick={() => setActiveMode("images")}
                      title="Create your own character with AI"
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        borderRadius: "10px",
                        overflow: "hidden",
                        padding: 0,
                        cursor: "pointer",
                        border: `1.5px dashed ${PINK_DEEP}`,
                        background: `linear-gradient(135deg, #ffffff 0%, ${PINK_BG_SOFT} 100%)`,
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "2px",
                        color: PINK_DEEP,
                        transition: "transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = "translateY(-2px)"
                        e.currentTarget.style.boxShadow = "0 4px 10px rgba(255, 79, 184, 0.20)"
                        e.currentTarget.style.background = `linear-gradient(135deg, ${PINK_BG_SOFT} 0%, #fce4ec 100%)`
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "translateY(0)"
                        e.currentTarget.style.boxShadow = "none"
                        e.currentTarget.style.background = `linear-gradient(135deg, #ffffff 0%, ${PINK_BG_SOFT} 100%)`
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        stroke="none"
                        aria-hidden="true"
                      >
                        <path d="M11.5 2a.5.5 0 0 1 .47.34l1.4 4.18a3 3 0 0 0 1.9 1.9l4.18 1.4a.5.5 0 0 1 0 .96l-4.18 1.4a3 3 0 0 0-1.9 1.9l-1.4 4.18a.5.5 0 0 1-.96 0l-1.4-4.18a3 3 0 0 0-1.9-1.9L3.51 11.78a.5.5 0 0 1 0-.96l4.18-1.4a3 3 0 0 0 1.9-1.9l1.4-4.18A.5.5 0 0 1 11.5 2z" />
                      </svg>
                      <span
                        style={{
                          fontSize: "8px",
                          fontWeight: 700,
                          lineHeight: 1.1,
                          textAlign: "center",
                          padding: "0 2px",
                        }}
                      >
                        Create<br />your own
                      </span>
                    </button>
                    {characterGalleryTemplates.length === 0 ? (
                      <div
                        style={{
                          gridColumn: "2 / -1",
                          fontSize: "11px",
                          color: TEXT_MUTED,
                          lineHeight: 1.4,
                          textAlign: "center",
                          padding: "12px 8px",
                          alignSelf: "center",
                        }}
                      >
                        Loading examples…
                      </div>
                    ) : (
                      characterGalleryTemplates.map((tpl) => {
                        const tplUrl = tpl.url || tpl.thumbnailUrl
                        const isPicked = characterImageUrl === tplUrl
                        return (
                          <button
                            key={tpl.id}
                            type="button"
                            onClick={() => tplUrl && setCharacterImageUrl(tplUrl)}
                            title={tpl.style || tpl.name || tpl.label || "Use this character"}
                            style={{
                              width: "100%",
                              aspectRatio: "1 / 1",
                              borderRadius: "10px",
                              overflow: "hidden",
                              padding: 0,
                              cursor: "pointer",
                              border: isPicked
                                ? `2px solid ${PINK_DEEP}`
                                : `1px solid ${PINK_BORDER}`,
                              background: "#fce4ec",
                              boxShadow: isPicked
                                ? "0 4px 10px rgba(255, 79, 184, 0.30)"
                                : "none",
                              position: "relative",
                              transition: "transform 0.15s ease, box-shadow 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (isPicked) return
                              e.currentTarget.style.transform = "translateY(-2px)"
                              e.currentTarget.style.boxShadow = "0 4px 10px rgba(74, 26, 44, 0.15)"
                            }}
                            onMouseLeave={(e) => {
                              if (isPicked) return
                              e.currentTarget.style.transform = "translateY(0)"
                              e.currentTarget.style.boxShadow = "none"
                            }}
                          >
                            <img
                              src={tpl.thumbnailUrl || tpl.url}
                              alt={tpl.style || tpl.name || "Example character"}
                              loading="lazy"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                            {isPicked && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "3px",
                                  right: "3px",
                                  width: "16px",
                                  height: "16px",
                                  borderRadius: "50%",
                                  background: PINK_DEEP,
                                  color: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "2px solid #fff",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                }}
                              >
                                <svg
                                  width="9"
                                  height="9"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              </span>
                            )}
                          </button>
                        )
                      })
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Motion Video — only when Motion Control mode is active */}
            {activeMode === "motion-control" && (
            <div>
              <div style={sectionLabelStyle}>
                <span>Add Motion Video</span>
              </div>
              <div style={motionGridStyle}>
                <MotionTile
                  variant="upload"
                  busy={uploadBusy}
                  isActive={false}
                  onClick={() => motionInputRef.current?.click()}
                />
                <MotionTile
                  variant="tiktok"
                  busy={tiktokBusy}
                  isActive={tiktokPanelOpen}
                  onClick={() =>
                    tiktokPanelOpen
                      ? setTiktokPanelOpen(false)
                      : openTikTokPanel()
                  }
                />
                {savedMotionVideos.map((savedItem) => (
                  <MotionTile
                    key={savedItem.id}
                    variant="saved"
                    savedItem={savedItem}
                    isActive={customMotionUrl === savedItem.url}
                    onClick={() => handleSelectSavedMotion(savedItem)}
                    onDelete={() => handleDeleteSavedMotion(savedItem)}
                  />
                ))}
                {MOTION_EXAMPLES.map((tile) => (
                  <MotionTile
                    key={tile.id}
                    tile={tile}
                    variant="example"
                    isActive={
                      !customMotionUrl &&
                      !tiktokPanelOpen &&
                      !uploadBusy &&
                      !tiktokBusy &&
                      selectedMotion?.id === tile.id
                    }
                    onClick={() => {
                      setCustomMotionUrl(null)
                      setCustomMotionLabel("")
                      setCustomMotionSource(null)
                      setTiktokPanelOpen(false)
                      setSelectedMotion(tile)
                    }}
                  />
                ))}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: TEXT_MUTED,
                  marginTop: "8px",
                  lineHeight: 1.4,
                }}
              >
                Max {MOTION_VIDEO_MAX_SECONDS}s. Longer videos are auto-trimmed
                to {MOTION_VIDEO_MAX_SECONDS}s.
              </div>

              {tiktokPanelOpen && (
                <div
                  style={{
                    marginTop: "10px",
                    padding: "12px",
                    borderRadius: "12px",
                    border: `1px solid ${PINK_BORDER}`,
                    background: PINK_BG_SOFT,
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: TEXT_MUTED,
                      letterSpacing: "0.4px",
                      textTransform: "uppercase",
                    }}
                  >
                    Paste TikTok link
                  </div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "stretch" }}>
                    <input
                      ref={tiktokInputRef}
                      type="text"
                      value={tiktokUrl}
                      onChange={(e) => setTiktokUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !tiktokBusy) handleTikTokImport()
                        if (e.key === "Escape") setTiktokPanelOpen(false)
                      }}
                      disabled={tiktokBusy}
                      placeholder="https://www.tiktok.com/@user/video/…"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        padding: "10px 12px",
                        borderRadius: "10px",
                        border: `1px solid ${PINK_BORDER}`,
                        background: "#ffffff",
                        fontSize: "13px",
                        color: TEXT_DARK,
                        fontFamily: "inherit",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                      onFocus={(e) =>
                        (e.currentTarget.style.borderColor = PINK_DEEP)
                      }
                      onBlur={(e) =>
                        (e.currentTarget.style.borderColor = PINK_BORDER)
                      }
                    />
                    <button
                      type="button"
                      onClick={handleTikTokImport}
                      disabled={tiktokBusy || !tiktokUrl.trim()}
                      style={{
                        padding: "0 14px",
                        borderRadius: "10px",
                        border: "none",
                        cursor:
                          tiktokBusy || !tiktokUrl.trim()
                            ? "not-allowed"
                            : "pointer",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "13px",
                        background: tiktokBusy
                          ? "linear-gradient(135deg, #c79bb1 0%, #d8a8c2 100%)"
                          : `linear-gradient(135deg, ${PINK} 0%, #ff85cc 100%)`,
                        opacity: !tiktokUrl.trim() && !tiktokBusy ? 0.55 : 1,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                      }}
                    >
                      {tiktokBusy ? "Importing…" : "Import"}
                    </button>
                  </div>
                  {tiktokBusy && (
                    <div style={{ fontSize: "11px", color: TEXT_MUTED }}>
                      Downloading from TikTok… can take 10–30 s.
                    </div>
                  )}
                  {tiktokError && (
                    <div
                      style={{
                        background: "#FEF2F2",
                        border: "1px solid #FCA5A5",
                        color: "#B91C1C",
                        padding: "8px 10px",
                        borderRadius: "8px",
                        fontSize: "12px",
                        lineHeight: 1.4,
                      }}
                    >
                      {tiktokError}
                    </div>
                  )}
                  <div
                    style={{
                      fontSize: "10px",
                      color: TEXT_MUTED,
                      lineHeight: 1.4,
                    }}
                  >
                    Public TikTok videos only. Private or region-locked videos
                    can't be imported. Videos longer than{" "}
                    {MOTION_VIDEO_MAX_SECONDS}s are auto-trimmed to{" "}
                    {MOTION_VIDEO_MAX_SECONDS}s.
                  </div>
                </div>
              )}

              {uploadError && (
                <div
                  style={{
                    marginTop: "8px",
                    background: "#FEF2F2",
                    border: "1px solid #FCA5A5",
                    color: "#B91C1C",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    fontSize: "12px",
                    lineHeight: 1.4,
                  }}
                >
                  {uploadError}
                </div>
              )}

              {uploadBusy && uploadStatus && (
                <div
                  style={{
                    marginTop: "8px",
                    background: PINK_BG_SOFT,
                    border: `1px solid ${PINK_BORDER}`,
                    color: PINK_DEEP,
                    padding: "8px 10px",
                    borderRadius: "8px",
                    fontSize: "12px",
                    fontWeight: 600,
                    lineHeight: 1.4,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      border: `2px solid ${PINK_BORDER}`,
                      borderTopColor: PINK_DEEP,
                      borderRadius: "50%",
                      animation: "pinkcatai-spin 0.8s linear infinite",
                      display: "inline-block",
                      flexShrink: 0,
                    }}
                  />
                  {uploadStatus}
                </div>
              )}

              <input
                ref={motionInputRef}
                type="file"
                accept="video/mp4,video/quicktime,video/webm,video/x-matroska,video/x-msvideo,.mp4,.mov,.webm,.mkv,.avi"
                style={{ display: "none" }}
                onChange={handleModalUploadFile}
              />
            </div>
            )}

            {/* Describe your video (Advanced) — only when Motion Control mode is active */}
            {activeMode === "motion-control" && (
              <div style={{ marginTop: "16px" }}>
                <button
                  type="button"
                  onClick={() => setMotionAdvancedOpen((v) => !v)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `1px solid ${PINK_BORDER}`,
                    borderRadius: motionAdvancedOpen ? "10px 10px 0 0" : "10px",
                    background: PINK_BG_SOFT,
                    color: TEXT_MUTED,
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.4px",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>Advanced</span>
                    <span
                      style={{
                        fontSize: "9px",
                        fontWeight: 600,
                        color: TEXT_MUTED,
                        opacity: 0.7,
                        letterSpacing: "0.3px",
                      }}
                    >
                      describe your video · optional
                    </span>
                  </span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      transform: motionAdvancedOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.15s ease",
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {motionAdvancedOpen && (
                  <div
                    style={{
                      border: `1px solid ${PINK_BORDER}`,
                      borderTop: "none",
                      borderRadius: "0 0 10px 10px",
                      overflow: "hidden",
                      background: "#ffffff",
                      boxSizing: "border-box",
                    }}
                  >
                    {/* Model selector: Pro (recommended) vs Basic */}
                    <div
                      style={{
                        padding: "10px 12px",
                        borderBottom: `1px solid ${PINK_BORDER}`,
                        background: "#ffffff",
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "10px",
                          fontWeight: 700,
                          color: TEXT_MUTED,
                          letterSpacing: "0.4px",
                          textTransform: "uppercase",
                        }}
                      >
                        Model
                      </div>
                      <div
                        role="radiogroup"
                        aria-label="Motion model"
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "6px",
                        }}
                      >
                        {[
                          {
                            id: "pro",
                            label: "Pro",
                            sub: "Recommended · best quality · 1072p",
                            cost: VIDEO_CREDIT_COST,
                          },
                          {
                            id: "basic",
                            label: "Basic",
                            sub: "Faster · 720p",
                            cost: VIDEO_CREDIT_COST_BASIC,
                          },
                        ].map((m) => {
                          const isActive = motionModelTier === m.id
                          return (
                            <button
                              key={m.id}
                              type="button"
                              role="radio"
                              aria-checked={isActive}
                              onClick={() => setMotionModelTier(m.id)}
                              style={{
                                padding: "8px 10px",
                                borderRadius: "10px",
                                border: isActive
                                  ? `2px solid ${PINK_DEEP}`
                                  : `1px solid ${PINK_BORDER}`,
                                background: isActive ? PINK_BG_SOFT : "#ffffff",
                                color: TEXT_DARK,
                                cursor: "pointer",
                                textAlign: "left",
                                display: "flex",
                                flexDirection: "column",
                                gap: "2px",
                                fontFamily: "inherit",
                                boxShadow: isActive
                                  ? "0 2px 6px rgba(255, 79, 184, 0.15)"
                                  : "none",
                                transition: "border-color 0.15s ease, background 0.15s ease",
                              }}
                            >
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "6px",
                                  fontSize: "12px",
                                  fontWeight: 700,
                                  color: isActive ? PINK_DEEP : TEXT_DARK,
                                }}
                              >
                                <span>{m.label}</span>
                                <span
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    color: isActive ? PINK_DEEP : TEXT_MUTED,
                                    background: isActive ? "#ffffff" : PINK_BG_SOFT,
                                    border: `1px solid ${PINK_BORDER}`,
                                    padding: "1px 6px",
                                    borderRadius: "999px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {m.cost} cr
                                </span>
                              </span>
                              <span
                                style={{
                                  fontSize: "10px",
                                  color: TEXT_MUTED,
                                  lineHeight: 1.3,
                                }}
                              >
                                {m.sub}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    </div>

                    <div style={{ position: "relative" }}>
                      <textarea
                        value={motionPrompt}
                        onChange={(e) =>
                          setMotionPrompt(e.target.value.slice(0, 500))
                        }
                        placeholder="What should happen in the video? e.g. Barbie performs, background moves…"
                        style={{
                          width: "100%",
                          minHeight: "72px",
                          padding: "10px 36px 10px 12px",
                          borderRadius: 0,
                          border: "none",
                          background: "transparent",
                          fontSize: "13px",
                          lineHeight: 1.4,
                          color: TEXT_DARK,
                          fontFamily: "inherit",
                          outline: "none",
                          resize: "vertical",
                          boxSizing: "border-box",
                          display: "block",
                        }}
                      />
                      {motionPrompt && (
                        <button
                          type="button"
                          title="Clear prompt"
                          onClick={() => {
                            setMotionPrompt("")
                            setSelectedMotionTags([])
                          }}
                          style={{
                            position: "absolute",
                            top: "8px",
                            right: "8px",
                            width: "24px",
                            height: "24px",
                            borderRadius: "6px",
                            border: `1px solid ${PINK_BORDER}`,
                            background: "#ffffff",
                            color: PINK_DEEP,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                            <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px",
                        maxHeight: "120px",
                        overflowY: "auto",
                        padding: "8px",
                        borderTop: `1px solid ${PINK_BORDER}`,
                        background: PINK_BG_SOFT,
                        alignContent: "flex-start",
                      }}
                    >
                      {[
                        { tag: "Background moves", kind: "bg" },
                        { tag: "Camera pans", kind: "bg" },
                        { tag: "Slow zoom in", kind: "bg" },
                        { tag: "Barbie performs", kind: "action" },
                        { tag: "Hair flow", kind: "action" },
                        { tag: "Sparkle particles", kind: "fx" },
                        { tag: "Cinematic lighting", kind: "fx" },
                        { tag: "Slow motion", kind: "fx" },
                      ].map(({ tag, kind }) => {
                        const isActive = selectedMotionTags.includes(tag)
                        const palettes = {
                          bg: {
                            active: { bg: "#7aab63", border: "#5a8d44", color: "#ffffff" },
                            idle: { bg: "#e6f5e0", border: "#b8e0a8", color: "#3f7a2c" },
                          },
                          action: {
                            active: { bg: "#5a7da8", border: "#3f5d88", color: "#ffffff" },
                            idle: { bg: "#e0eaf5", border: "#a8c4e0", color: "#2c4f7a" },
                          },
                          fx: {
                            active: { bg: "#c25450", border: "#a03a36", color: "#ffffff" },
                            idle: { bg: "#fbe5e3", border: "#f3b8b4", color: "#a03a36" },
                          },
                        }
                        const palette = isActive ? palettes[kind].active : palettes[kind].idle
                        return (
                          <button
                            key={tag}
                            type="button"
                            onClick={() => {
                              if (isActive) {
                                setSelectedMotionTags((prev) =>
                                  prev.filter((t) => t !== tag)
                                )
                                let text = motionPrompt
                                if (text.includes(`, ${tag}`)) text = text.replace(`, ${tag}`, "")
                                else if (text.includes(`${tag}, `)) text = text.replace(`${tag}, `, "")
                                else text = text.replace(tag, "")
                                text = text.trim().replace(/^,\s*/, "").replace(/,\s*$/, "")
                                setMotionPrompt(text.slice(0, 500))
                              } else {
                                setSelectedMotionTags((prev) => [...prev, tag])
                                const current = motionPrompt.trim()
                                const next = current ? `${current}, ${tag}` : tag
                                setMotionPrompt(next.slice(0, 500))
                              }
                            }}
                            style={{
                              padding: "2px 7px",
                              borderRadius: "999px",
                              border: `1px solid ${palette.border}`,
                              backgroundColor: palette.bg,
                              color: palette.color,
                              fontSize: "10px",
                              fontWeight: 500,
                              cursor: "pointer",
                              whiteSpace: "nowrap",
                              lineHeight: 1.3,
                              transition: "all 0.15s ease",
                            }}
                          >
                            {isActive ? "✓" : "+"} {tag}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Describe your character — only when Images mode is active */}
            {activeMode === "images" && (
              <div>
                {/* Trending Character */}
                <div style={sectionLabelStyle}>
                  <span>Trending Character</span>
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: 600,
                      color: TEXT_MUTED,
                      opacity: 0.7,
                      letterSpacing: "0.3px",
                      textTransform: "uppercase",
                    }}
                  >
                    optional
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "6px",
                    marginBottom: "8px",
                  }}
                >
                  {[
                    { id: "none", label: "None" },
                    ...CHARACTERS.map((c) => ({
                      id: c.id,
                      label: c.label,
                    })),
                  ].map((c) => {
                    const isActive =
                      c.id === "none"
                        ? selectedCharacterId === null ||
                          selectedCharacterId === "none"
                        : selectedCharacterId === c.id
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          let text = imagePrompt
                          for (const p of ALL_CHARACTER_PHRASES) {
                            if (text.startsWith(`${p}, `))
                              text = text.slice(`${p}, `.length)
                            else if (text.startsWith(p))
                              text = text.slice(p.length)
                            else if (text.includes(`, ${p}`))
                              text = text.replace(`, ${p}`, "")
                            else if (text.includes(`${p}, `))
                              text = text.replace(`${p}, `, "")
                            else text = text.replace(p, "")
                          }
                          text = text
                            .trim()
                            .replace(/^,\s*/, "")
                            .replace(/,\s*$/, "")

                          if (c.id === "none") {
                            setSelectedCharacterId(null)
                            setTransferPeople(false)
                            setImagePrompt(text.slice(0, 500))
                            return
                          }
                          if (isActive) {
                            setSelectedCharacterId(null)
                            setTransferPeople(false)
                            setImagePrompt(text.slice(0, 500))
                            return
                          }
                          const newPhrase = characterPhraseFor(
                            c.id,
                            transferPeople
                          )
                          const next = text.trim()
                            ? `${newPhrase}, ${text.trim()}`
                            : newPhrase
                          setSelectedCharacterId(c.id)
                          setImagePrompt(next.slice(0, 500))
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          padding: "5px 11px",
                          borderRadius: "999px",
                          border: isActive
                            ? `1px solid ${PINK_DEEP}`
                            : `1px solid ${PINK_BORDER}`,
                          background: isActive
                            ? `linear-gradient(135deg, ${PINK} 0%, #ff85cc 100%)`
                            : "#ffffff",
                          color: isActive ? "#ffffff" : TEXT_DARK,
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          lineHeight: 1.2,
                          boxShadow: isActive
                            ? "0 4px 10px rgba(255, 79, 184, 0.30)"
                            : "none",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {isActive && (
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                        {c.label}
                      </button>
                    )
                  })}
                </div>

                {/* Transfer-people toggle */}
                {(() => {
                  const enabled = !!selectedCharacterId
                  const toggleTransfer = () => {
                    if (!enabled) return
                    const nextOn = !transferPeople
                    let text = imagePrompt
                    for (const p of ALL_CHARACTER_PHRASES) {
                      if (text.startsWith(`${p}, `))
                        text = text.slice(`${p}, `.length)
                      else if (text.startsWith(p))
                        text = text.slice(p.length)
                      else if (text.includes(`, ${p}`))
                        text = text.replace(`, ${p}`, "")
                      else if (text.includes(`${p}, `))
                        text = text.replace(`${p}, `, "")
                      else text = text.replace(p, "")
                    }
                    text = text
                      .trim()
                      .replace(/^,\s*/, "")
                      .replace(/,\s*$/, "")
                    const newPhrase = characterPhraseFor(
                      selectedCharacterId,
                      nextOn
                    )
                    const next = text.trim()
                      ? `${newPhrase}, ${text.trim()}`
                      : newPhrase
                    setTransferPeople(nextOn)
                    setImagePrompt(next.slice(0, 500))
                  }
                  return (
                    <button
                      type="button"
                      onClick={toggleTransfer}
                      disabled={!enabled}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "100%",
                        padding: "8px 10px",
                        marginBottom: "14px",
                        borderRadius: "10px",
                        border: `1px dashed ${PINK_BORDER}`,
                        background: enabled ? "#ffffff" : PINK_BG_SOFT,
                        cursor: enabled ? "pointer" : "not-allowed",
                        opacity: enabled ? 1 : 0.6,
                        textAlign: "left",
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          position: "relative",
                          display: "inline-block",
                          flexShrink: 0,
                          width: "32px",
                          height: "18px",
                          borderRadius: "999px",
                          background:
                            transferPeople && enabled
                              ? `linear-gradient(135deg, ${PINK} 0%, #ff85cc 100%)`
                              : "#e5d3dc",
                          transition: "background 0.15s ease",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            top: "2px",
                            left:
                              transferPeople && enabled
                                ? "16px"
                                : "2px",
                            width: "14px",
                            height: "14px",
                            borderRadius: "50%",
                            background: "#ffffff",
                            boxShadow:
                              "0 1px 3px rgba(0,0,0,0.18)",
                            transition: "left 0.15s ease",
                          }}
                        />
                      </span>
                      <span
                        style={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: TEXT_DARK,
                          lineHeight: 1.3,
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        Transfer the people in image into this character
                      </span>
                    </button>
                  )
                })()}

                {/* Reference image */}
                <div style={sectionLabelStyle}>
                  <span>Reference image</span>
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: 600,
                      color: TEXT_MUTED,
                      opacity: 0.7,
                      letterSpacing: "0.3px",
                      textTransform: "uppercase",
                    }}
                  >
                    optional
                  </span>
                </div>
                <div
                  style={{
                    width: "100%",
                    height: "132px",
                    borderRadius: "12px",
                    border: `1px solid ${PINK_BORDER}`,
                    background: PINK_BG_SOFT,
                    padding: "6px",
                    overflowX: "hidden",
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "thin",
                    boxShadow: "inset 0 1px 3px rgba(74,26,44,0.06)",
                    marginBottom: "14px",
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {(() => {
                      const isTemplateSelected =
                        orientationImageUrl &&
                        orientationTemplates.some(
                          (t) => t.fullUrl === orientationImageUrl
                        )
                      // R2-URL bevorzugen, sonst lokale Blob-Vorschau (für
                      // sofortiges Feedback auf Mobile vor dem Login).
                      const orientationDisplayUrl =
                        (!isTemplateSelected && orientationImageUrl) ||
                        orientationLocalPreview
                      const hasCustomUpload = !!orientationDisplayUrl
                      return (
                        <button
                          type="button"
                          onClick={() =>
                            orientationInputRef.current?.click()
                          }
                          disabled={orientationUploading}
                          onDragEnter={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (!orientationUploading) setOrientationDragOver(true)
                          }}
                          onDragOver={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"
                            if (!orientationUploading && !orientationDragOver) setOrientationDragOver(true)
                          }}
                          onDragLeave={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            if (e.currentTarget.contains(e.relatedTarget)) return
                            setOrientationDragOver(false)
                          }}
                          onDrop={handleOrientationDrop}
                          style={{
                            width: "92px",
                            height: "92px",
                            borderRadius: "12px",
                            border: orientationDragOver
                              ? `2px dashed ${PINK_DEEP}`
                              : hasCustomUpload
                              ? `2px solid ${PINK_DEEP}`
                              : `2px dashed ${PINK_BORDER}`,
                            background: orientationDragOver
                              ? "#fce4ec"
                              : `linear-gradient(135deg, #ffffff 0%, #fce4ec 100%)`,
                            position: "relative",
                            cursor: orientationUploading
                              ? "wait"
                              : "pointer",
                            overflow: "hidden",
                            flexShrink: 0,
                            padding: 0,
                            boxShadow: hasCustomUpload || orientationDragOver
                              ? "0 4px 10px rgba(255, 79, 184, 0.30)"
                              : "none",
                            transition:
                              "background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
                          }}
                        >
                          {hasCustomUpload ? (
                            <img
                              src={orientationDisplayUrl}
                              alt="Uploaded reference"
                              style={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "4px",
                                height: "100%",
                                width: "100%",
                              }}
                            >
                              <div
                                style={{
                                  width: "34px",
                                  height: "34px",
                                  borderRadius: "50%",
                                  background: `linear-gradient(135deg, ${PINK} 0%, #ff85cc 100%)`,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow:
                                    "0 4px 10px rgba(255, 79, 184, 0.30)",
                                }}
                              >
                                <svg
                                  width="17"
                                  height="17"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="#fff"
                                  strokeWidth="2.4"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="17 8 12 3 7 8" />
                                  <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                              </div>
                              <span
                                style={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: TEXT_DARK,
                                  lineHeight: 1,
                                }}
                              >
                                {orientationUploading ? "…" : "Upload"}
                              </span>
                            </div>
                          )}
                          {hasCustomUpload && (
                            <span
                              onClick={(e) => {
                                e.stopPropagation()
                                updateOrientation(null)
                                setOrientationLocalPreview((prev) => {
                                  if (prev) {
                                    try { URL.revokeObjectURL(prev) } catch (_) {}
                                  }
                                  return null
                                })
                                pendingOrientationFileRef.current = null
                              }}
                              title="Remove uploaded image"
                              style={{
                                position: "absolute",
                                top: "3px",
                                left: "3px",
                                width: "18px",
                                height: "18px",
                                borderRadius: "50%",
                                background: "rgba(0,0,0,0.6)",
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "13px",
                                cursor: "pointer",
                                lineHeight: 1,
                                border: "1.5px solid #fff",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                                zIndex: 2,
                              }}
                            >
                              ×
                            </span>
                          )}
                          {hasCustomUpload && (
                            <span
                              style={{
                                position: "absolute",
                                top: "3px",
                                right: "3px",
                                width: "16px",
                                height: "16px",
                                borderRadius: "50%",
                                background: PINK_DEEP,
                                color: "#fff",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "11px",
                                cursor: "pointer",
                                lineHeight: 1,
                                border: "2px solid #fff",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                zIndex: 2,
                              }}
                            >
                              <svg
                                width="9"
                                height="9"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            </span>
                          )}
                          <input
                            ref={orientationInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={handleOrientationFile}
                          />
                        </button>
                      )
                    })()}
                    {orientationTemplates.map((tpl) => {
                        const isActive = orientationImageUrl === tpl.fullUrl
                        return (
                          <button
                            key={tpl.id}
                            type="button"
                            onClick={() => {
                              updateOrientation(
                                isActive ? null : tpl.fullUrl
                              )
                            }}
                            style={{
                              width: "80px",
                              height: "80px",
                              flexShrink: 0,
                              borderRadius: "10px",
                              border: isActive
                                ? `2px solid ${PINK_DEEP}`
                                : `1px solid ${PINK_BORDER}`,
                              background: "#fce4ec",
                              padding: 0,
                              overflow: "hidden",
                              position: "relative",
                              cursor: "pointer",
                              boxShadow: isActive
                                ? "0 4px 10px rgba(255, 79, 184, 0.30)"
                                : "none",
                              transition: "transform 0.15s ease, box-shadow 0.15s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (isActive) return
                              e.currentTarget.style.transform = "translateY(-2px)"
                              e.currentTarget.style.boxShadow = "0 4px 10px rgba(74, 26, 44, 0.15)"
                            }}
                            onMouseLeave={(e) => {
                              if (isActive) return
                              e.currentTarget.style.transform = "translateY(0)"
                              e.currentTarget.style.boxShadow = "none"
                            }}
                          >
                            <img
                              src={tpl.thumbUrl}
                              alt="Reference image example"
                              loading="lazy"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                            {isActive && (
                              <span
                                style={{
                                  position: "absolute",
                                  top: "3px",
                                  right: "3px",
                                  width: "16px",
                                  height: "16px",
                                  borderRadius: "50%",
                                  background: PINK_DEEP,
                                  color: "#fff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  border: "2px solid #fff",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                                }}
                              >
                                <svg
                                  width="9"
                                  height="9"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                              </span>
                            )}
                          </button>
                        )
                      })}
                  </div>
                </div>

                <div style={sectionLabelStyle}>
                  <span>Describe your character</span>
                </div>

                {/* Prompt textarea + tag chips — joined unit */}
                <div
                  style={{
                    border: `1px solid ${PINK_BORDER}`,
                    borderRadius: "10px",
                    overflow: "hidden",
                    background: "#ffffff",
                    boxSizing: "border-box",
                  }}
                >
                  <div style={{ position: "relative" }}>
                    <textarea
                      value={imagePrompt}
                      onChange={(e) =>
                        setImagePrompt(e.target.value.slice(0, 500))
                      }
                      placeholder="A barbie doll with long blond hair, glamorous makeup, posing in a pink castle…"
                      style={{
                        width: "100%",
                        minHeight: "92px",
                        padding: "10px 36px 10px 12px",
                        borderRadius: 0,
                        border: "none",
                        background: "transparent",
                        fontSize: "13px",
                        lineHeight: 1.4,
                        color: TEXT_DARK,
                        fontFamily: "inherit",
                        outline: "none",
                        resize: "vertical",
                        boxSizing: "border-box",
                        display: "block",
                      }}
                    />
                    {imagePrompt && (
                      <button
                        type="button"
                        title="Clear prompt"
                        onClick={() => {
                          setImagePrompt("")
                          setSelectedImageTags([])
                          setSelectedImageFilterId(null)
                          setSelectedCharacterId(null)
                          setTransferPeople(false)
                          // Bewusst NICHT das ausgewählte/gedroppte Reference-
                          // Image löschen – der Trash-Button räumt nur den
                          // Prompt + zugehörige Tags/Filter/Character auf.
                        }}
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          width: "24px",
                          height: "24px",
                          borderRadius: "6px",
                          border: `1px solid ${PINK_BORDER}`,
                          background: "#ffffff",
                          color: PINK_DEEP,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 0,
                        }}
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6" />
                          <path d="M14 11v6" />
                          <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Tag chips (background / look / fx) — scrollable */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                      maxHeight: "78px",
                      overflowY: "auto",
                      padding: "6px 8px",
                      borderTop: `1px solid ${PINK_BORDER}`,
                      background: PINK_BG_SOFT,
                      alignContent: "flex-start",
                    }}
                  >
                  {[
                    { tag: "Pink castle background", kind: "bg" },
                    { tag: "Beach in Malibu", kind: "bg" },
                    { tag: "Disco club lights", kind: "bg" },
                    { tag: "Dreamhouse interior", kind: "bg" },
                    { tag: "Make her blond", kind: "look" },
                    { tag: "Add a tiara", kind: "look" },
                    { tag: "Glamorous makeup", kind: "look" },
                    { tag: "Sparkly pink dress", kind: "look" },
                    { tag: "Film grain", kind: "fx" },
                    { tag: "Floating glitter", kind: "fx" },
                    { tag: "Dust particles in the air", kind: "fx" },
                    { tag: "Light leaks", kind: "fx" },
                    { tag: "Lens flare", kind: "fx" },
                    { tag: "Bokeh sparkles", kind: "fx" },
                  ].map(({ tag, kind }) => {
                    const isActive = selectedImageTags.includes(tag)
                    const palettes = {
                      bg: {
                        active: { bg: "#7aab63", border: "#5a8d44", color: "#ffffff" },
                        idle: { bg: "#e6f5e0", border: "#b8e0a8", color: "#3f7a2c" },
                      },
                      look: {
                        active: { bg: "#5a7da8", border: "#3f5d88", color: "#ffffff" },
                        idle: { bg: "#e0eaf5", border: "#a8c4e0", color: "#2c4f7a" },
                      },
                      fx: {
                        active: { bg: "#c25450", border: "#a03a36", color: "#ffffff" },
                        idle: { bg: "#fbe5e3", border: "#f3b8b4", color: "#a03a36" },
                      },
                    }
                    const palette = isActive ? palettes[kind].active : palettes[kind].idle
                    return (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => {
                          if (isActive) {
                            setSelectedImageTags((prev) =>
                              prev.filter((t) => t !== tag)
                            )
                            let text = imagePrompt
                            if (text.includes(`, ${tag}`)) text = text.replace(`, ${tag}`, "")
                            else if (text.includes(`${tag}, `)) text = text.replace(`${tag}, `, "")
                            else text = text.replace(tag, "")
                            text = text.trim().replace(/^,\s*/, "").replace(/,\s*$/, "")
                            setImagePrompt(text.slice(0, 500))
                          } else {
                            setSelectedImageTags((prev) => [...prev, tag])
                            const current = imagePrompt.trim()
                            const next = current ? `${current}, ${tag}` : tag
                            setImagePrompt(next.slice(0, 500))
                          }
                        }}
                        style={{
                          padding: "2px 7px",
                          borderRadius: "999px",
                          border: `1px solid ${palette.border}`,
                          backgroundColor: palette.bg,
                          color: palette.color,
                          fontSize: "10px",
                          fontWeight: 500,
                          cursor: "pointer",
                          whiteSpace: "nowrap",
                          lineHeight: 1.3,
                          transition: "all 0.15s ease",
                        }}
                      >
                        {isActive ? "✓" : "+"} {tag}
                      </button>
                    )
                  })}
                  </div>
                </div>

                {/* Style filter cards */}
                <div
                  style={{
                    marginTop: "12px",
                    fontSize: "11px",
                    fontWeight: 700,
                    color: TEXT_MUTED,
                    letterSpacing: "0.4px",
                    textTransform: "uppercase",
                    marginBottom: "4px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span>Style</span>
                  <span
                    style={{
                      fontSize: "9px",
                      fontWeight: 600,
                      color: TEXT_MUTED,
                      opacity: 0.7,
                      letterSpacing: "0.3px",
                      textTransform: "uppercase",
                    }}
                  >
                    optional
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                  }}
                >
                  {[
                    {
                      id: "none",
                      label: "No style",
                      prompt: null,
                      thumb: null,
                      gradient: "#ffffff",
                      isNone: true,
                    },
                    {
                      id: "retro",
                      label: "Retro",
                      prompt: "make the image like a 50s retro photo",
                      thumb:
                        exampleStyleTemplates[2]?.thumbnailUrl ||
                        exampleStyleTemplates[2]?.url ||
                        null,
                      gradient:
                        "linear-gradient(135deg, #f4b873 0%, #c25450 100%)",
                    },
                    {
                      id: "modern",
                      label: "Modern",
                      prompt:
                        "give the image a glossy high-fashion look with bright flash photography lighting and a polished editorial sheen",
                      thumb:
                        exampleStyleTemplates[1]?.thumbnailUrl ||
                        exampleStyleTemplates[1]?.url ||
                        null,
                      gradient:
                        "linear-gradient(135deg, #d3d3d3 0%, #4a1a2c 100%)",
                    },
                    {
                      id: "fantasy",
                      label: "Fantasy",
                      prompt:
                        "make everything in a fairy style with floating glitter and sparkles drifting through the air, magical dreamy atmosphere",
                      thumb:
                        exampleStyleTemplates[0]?.thumbnailUrl ||
                        exampleStyleTemplates[0]?.url ||
                        null,
                      gradient: `linear-gradient(135deg, ${PINK} 0%, #ffb15a 100%)`,
                    },
                  ].map((f) => {
                    const isSelected =
                      f.id === "none"
                        ? !selectedImageFilterId
                        : selectedImageFilterId === f.id
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => {
                          const all = [
                            "make the image like a 50s retro photo",
                            "give the image a glossy high-fashion look with bright flash photography lighting and a polished editorial sheen",
                            "make everything in a fairy style with floating glitter and sparkles drifting through the air, magical dreamy atmosphere",
                          ]
                          let text = imagePrompt
                          for (const p of all) {
                            if (text.includes(`, ${p}`)) text = text.replace(`, ${p}`, "")
                            else if (text.includes(`${p}, `)) text = text.replace(`${p}, `, "")
                            else text = text.replace(p, "")
                          }
                          text = text.trim().replace(/^,\s*/, "").replace(/,\s*$/, "")
                          if (f.isNone) {
                            setSelectedImageFilterId(null)
                            setImagePrompt(text.slice(0, 500))
                            return
                          }
                          if (isSelected) {
                            setSelectedImageFilterId(null)
                            setImagePrompt(text.slice(0, 500))
                            return
                          }
                          const trimmed = text.trim()
                          const next = trimmed ? `${trimmed}, ${f.prompt}` : f.prompt
                          setSelectedImageFilterId(f.id)
                          setImagePrompt(next.slice(0, 500))
                        }}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "stretch",
                          gap: 0,
                          padding: 0,
                          borderRadius: "8px",
                          cursor: "pointer",
                          backgroundColor: "#ffffff",
                          border: isSelected
                            ? `2px solid ${PINK_DEEP}`
                            : `1px solid ${PINK_BORDER}`,
                          overflow: "hidden",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            paddingTop: "75%",
                            position: "relative",
                            background: f.gradient,
                          }}
                        >
                          {f.thumb && (
                            <img
                              src={f.thumb}
                              alt={f.label}
                              loading="lazy"
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                            />
                          )}
                          {f.isNone && (
                            <span
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                color: PINK_DEEP,
                                fontSize: "16px",
                                lineHeight: 1,
                              }}
                              aria-hidden="true"
                            >
                              ⊘
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: "9px",
                            fontWeight: 600,
                            color: isSelected ? PINK_DEEP : TEXT_DARK,
                            textAlign: "center",
                            padding: "3px 2px 4px",
                            lineHeight: 1.1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {f.label}
                        </div>
                      </button>
                    )
                  })}
                </div>

              </div>
            )}

            {error && (
              <div
                style={{
                  background: "#FEF2F2",
                  border: "1px solid #FCA5A5",
                  color: "#B91C1C",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  lineHeight: 1.4,
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div style={leftFooterStyle}>{GenerateButton}</div>
        </div>

        {/* RIGHT: History */}
        <div style={rightColStyle}>
          <div style={rightHeaderStyle}>
            {imagesModeActive ? "Your Characters" : "Your Videos"}
          </div>
          <div style={rightBodyStyle}>
            <ImageGallery
              isLoadingInitial={isLoadingInitial}
              galleryImages={
                imagesModeActive ? visibleImageItems : visibleVideoItems
              }
              isMobile={isMobile}
              stacked
              setFullscreenImage={setFullscreenImage}
              onHideImage={handleHideImage}
              onUsePrompt={
                imagesModeActive ? handleGalleryUsePrompt : undefined
              }
              onUseForVideo={
                imagesModeActive ? handleGalleryUseForVideo : undefined
              }
              selectedVideoSourceUrl={
                imagesModeActive ? characterImageUrl : undefined
              }
              emptyText={
                imagesModeActive
                  ? "Your generated characters will appear here."
                  : "Your generated videos will appear here."
              }
              columns={isMobile ? 2 : 4}
            />
          </div>
        </div>
      </div>

      {fullscreenImage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            cursor: "zoom-out",
          }}
          onClick={() => setFullscreenImage(null)}
        >
          <img
            src={fullscreenImage}
            alt="Fullscreen view"
            style={{
              maxWidth: "90%",
              maxHeight: "90%",
              objectFit: "contain",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
            }}
          />
          <button
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "rgba(0,0,0,0.7)",
              color: "#fff",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              cursor: "pointer",
              fontSize: "20px",
              fontWeight: "bold",
              zIndex: 1001,
            }}
            onClick={() => setFullscreenImage(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  )
}

// Small inline icon component for credits — golden coin with a star
const CreditIcon = ({ size = 14, color }) => {
  const gradId = `creditCoinGradient-${size}`
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ flexShrink: 0, display: "block" }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffe27a" />
          <stop offset="55%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
      </defs>
      {/* Outer coin */}
      <circle cx="12" cy="12" r="10" fill={color || `url(#${gradId})`} stroke="#92400e" strokeWidth="1.2" />
      {/* Inner ring for depth */}
      <circle cx="12" cy="12" r="7.2" fill="none" stroke="#b45309" strokeWidth="0.8" opacity="0.55" />
      {/* Star in the centre */}
      <path
        d="M12 6.6 L13.45 10.2 L17.3 10.55 L14.4 13.05 L15.3 16.8 L12 14.85 L8.7 16.8 L9.6 13.05 L6.7 10.55 L10.55 10.2 Z"
        fill="#92400e"
      />
      {/* Subtle highlight */}
      <ellipse cx="9" cy="8.5" rx="2.6" ry="1.2" fill="#fff8d8" opacity="0.6" />
    </svg>
  )
}

export default Dashboard
